import {
  Fragment,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  Campaign,
  CampaignScopeCandidate,
  CatalogBaseProvenance,
  CatalogEdit,
  CatalogOptionEdit,
  CatalogOptionRow,
  CatalogPickItem,
  CatalogReviewItem,
  DerivedProfileSummary,
  FieldConfidence,
  MonthSummary,
  MoneyBreakdown,
  StoreResponse,
} from '@clarity/contract';
import type { OnboardingDeriveResult } from '@clarity/contract';
import { Legend } from '../components/Chart.js';
import {
  useCampaignScope,
  useCampaigns,
  useDeriveProfile,
  useDeriveProgress,
  useDerivedSummary,
  useEditCatalogFoodCost,
  useGather,
  useMergeCatalog,
  useOnboardingCatalog,
  useOnboardingStep1,
  usePlatformFeeDefaults,
  useRecalculate,
  useSaveOnboardingCatalog,
  useSetCampaignScope,
  useUnmergeCatalog,
} from '../queries/index.js';
import type { DeriveProgress, DeriveSourceStatus, OnboardingGatherResult } from '@clarity/contract';
import { catalogImageUrl, tsr } from '../lib/api.js';
import { platformLabels } from '../lib/platformLabels.js';
import { queryKeys } from '../queries/index.js';
import { QueryState } from './QueryState.js';
import { PayoutMatchPanel } from './payouts/PayoutMatchPanel.js';
import { errorStatus, money, num, pct, periodDates } from './format.js';
import { isNotYetOnboarded } from '../lib/lifecycle.js';
import { humanizeCareemCategory } from './careem-labels.js';

/**
 * The 3-step onboarding wizard (clarity-onboarding-ingestion Req 2.1, 2.2, 2.3).
 *
 * Step 1 — set `default_food_cost_pct` + `markup_pct`, confirm/override the
 * payment-gateway `pay_fee_*` params (Req 2.1 + Req 4.1).
 * Step 2 — review the resolved catalog; per item show food cost %, pre-markup
 * base price, and the derived platform price `base × (1 + markup)`; edit-or-
 * confirm each and persist (Req 2.2).
 * Step 3 — trigger the reference pull + derive, then show the read-only economic
 * profile; handle the `409` commission-unavailable block with the admin
 * `proceedAnyway` path (Req 2.3, 5.4, 6.1). Completing Step 3 sets the gate.
 *
 * Loading / error / the typed `{status, body}` union all come from the query
 * layer; this component owns only the step cursor + the per-step form state.
 */
/**
 * The wizard cursor — the leading `Data_Gathering_Step`, then the three steps,
 * with the `'campaigns'` item-scope step inserted between catalog review (step
 * 2) and the derived summary (step 3) (clarity-onboarding-campaign-scope Req
 * 3.1). `'campaigns'` is a string tag (not a number) so the numeric step order
 * of the original three steps is left untouched — only the stepper LABELS
 * renumber to show it as step 4 of 5.
 */
export type OnboardingStep = 'gather' | 1 | 2 | 'campaigns' | 3;

/**
 * Resume the wizard at the furthest step the store's persisted progress allows
 * (onboarding Req 2.1–2.3; unified-data-gathering Req 3.1, 8.3, 8.4), so
 * reopening a partially-onboarded store lands on the right step instead of
 * re-gathering. The `Data_Gathering_Step` now LEADS the wizard, so a store that
 * has not gathered yet starts there; everything past it resumes on the explicit
 * per-step confirmation flags (the baseline logic, preserved):
 *  - catalog confirmed (`catalogConfirmed`) or already complete
 *    (`onboardingComplete`) ⇒ Step 3 (derived summary);
 *  - Step 1 confirmed (`step1Confirmed`) ⇒ Step 2 (catalog review);
 *  - reference data already gathered (`referenceDataPresent`) but Step 1 not yet
 *    confirmed ⇒ Step 1 (the gather's "Continue to food cost") — distinct from
 *    the baseline, where `referenceDataPresent` jumped straight to Step 3,
 *    because the reference window is now pulled UP FRONT rather than at derive;
 *  - a gather currently `running` (server-derived from `ingestion_runs`,
 *    Req 8.3/8.5) ⇒ the gather step, which resumes into its LIVE loading state
 *    rather than the empty "start gathering" page;
 *  - otherwise ⇒ the gather step (empty / not started).
 * The user can still step Back from the resumed step.
 */
export function initialOnboardingStep(store: StoreResponse): OnboardingStep {
  // A fully-onboarded store lands on the summary. A store that has confirmed its
  // catalog but hasn't completed onboarding resumes on the new Campaigns step
  // (clarity-onboarding-campaign-scope Req 3.1) — it is skippable + idempotent
  // (reads live, writes an append-only version), so re-entering is safe and the
  // summary stays reachable via Continue/Skip. No new persisted marker is needed.
  if (store.onboardingComplete) return 3;
  if (store.catalogConfirmed) return 'campaigns';
  if (store.step1Confirmed) return 2;
  if (store.referenceDataPresent) return 1;
  // Not gathered yet — the gather step owns both the "running" resume (Req 8.3)
  // and the empty "start gathering" entry; it reads `store.gatherRunning` itself.
  return 'gather';
}

export function Onboarding({ store }: { store: StoreResponse }) {
  const [step, setStep] = useState<OnboardingStep>(() => initialOnboardingStep(store));

  return (
    <section>
      <h2>Onboarding — {store.externalStoreId}</h2>
      <ol className="wizard-steps" aria-label="Onboarding steps">
        <li
          aria-current={step === 'gather' ? 'step' : undefined}
          className={step === 'gather' ? 'step-active' : ''}
        >
          1. Gather data
        </li>
        <li aria-current={step === 1 ? 'step' : undefined} className={step === 1 ? 'step-active' : ''}>
          2. Food cost &amp; markup
        </li>
        <li aria-current={step === 2 ? 'step' : undefined} className={step === 2 ? 'step-active' : ''}>
          3. Catalog review
        </li>
        <li
          aria-current={step === 'campaigns' ? 'step' : undefined}
          className={step === 'campaigns' ? 'step-active' : ''}
        >
          4. Campaigns
        </li>
        <li aria-current={step === 3 ? 'step' : undefined} className={step === 3 ? 'step-active' : ''}>
          5. Summary
        </li>
      </ol>

      {step === 'gather' && <GatherStep store={store} onNext={() => setStep(1)} />}
      {step === 1 && <Step1 store={store} onNext={() => setStep(2)} />}
      {step === 2 && (
        <Step2 store={store} onBack={() => setStep(1)} onNext={() => setStep('campaigns')} />
      )}
      {step === 'campaigns' && (
        <CampaignsStep
          store={store}
          onBack={() => setStep(2)}
          onNext={() => setStep(3)}
        />
      )}
      {step === 3 && <Step3 store={store} onBack={() => setStep('campaigns')} />}
    </section>
  );
}

// ─── Campaigns step — human-entered item scope (clarity-onboarding-campaign-scope) ──

/**
 * Platform display + chip colour class for the Campaigns-step deal list. The
 * chip colour follows the mock (Talabat orange `tb`, Careem teal `cr`); an
 * unknown platform falls back to the neutral `tb` styling and its platform label.
 */
const CAMPAIGN_PLAT: Record<Campaign['platformKey'], { className: string; label: string }> = {
  talabat: { className: 'tb', label: 'Talabat' },
  careem: { className: 'cr', label: 'Careem' },
  mythings: { className: 'tb', label: 'MyThings' },
};

/**
 * A short terms line for a scoped-candidate deal, synthesized from its declared
 * discount terms (the mock's `terms` line, e.g. "20% off selected · cap 2 JOD").
 * The campaign has no free-text terms field, so we render the headline discount
 * (or its declared range), the item-scope note, and any cap — the same facts the
 * matcher reconciles against. `null`/0 terms are omitted so a sparse campaign
 * still reads cleanly.
 */
function campaignTermsLine(campaign: Campaign, currency: string | undefined): string {
  const parts: string[] = [];
  const asPct = (fraction: number) => pct(Math.round(fraction * 1000) / 10);
  if (campaign.discountPctMin !== null && campaign.discountPctMax !== null) {
    parts.push(`${asPct(campaign.discountPctMin)}–${asPct(campaign.discountPctMax)} off`);
  } else if (campaign.discountPct !== null) {
    parts.push(`${asPct(campaign.discountPct)} off`);
  }
  if (campaign.itemScoped || campaign.scopedProductIds.length > 0) {
    // Reads "… off selected" when there's a discount, else a standalone note.
    if (parts.length > 0) parts[0] = `${parts[0]} selected`;
    else parts.push('Selected items');
  }
  if (campaign.maxDiscountCap !== null && campaign.maxDiscountCap > 0) {
    parts.push(`cap ${money(campaign.maxDiscountCap, currency)}`);
  }
  return parts.join(' · ');
}

/** The human "why this is a candidate" line (the mock's `deal__why`), by reason. */
function candidateWhy(reason: CampaignScopeCandidate['reason']): string {
  return reason === 'already-scoped'
    ? 'This deal is already marked a selected-items deal — confirm or edit its items.'
    : 'The deal name signals a subset of your menu — the platform doesn’t list which items, so pick them here.';
}

/**
 * The onboarding **Campaigns** step (clarity-onboarding-campaign-scope Req 3),
 * reproducing the signed-off mock `brand/onboarding-campaigns-mock.html` on the
 * real design system. A human sets which catalog items a SELECTED-ITEMS deal
 * discounts, for the deals no platform API carries (Jam3at, all Careem
 * item-scoped promos). It is a READ of the persisted candidates + catalog and a
 * WRITE of one campaign's scope — NO pull (`ingestion-single-source`).
 *
 * Shape (the mock):
 *  - a heading + an informed **skip nudge** counting the candidate deals that
 *    still have no items (turns to an all-set confirmation when none remain);
 *  - the **scoped-candidate list** — each deal's name + platform badge + terms +
 *    the "why" line + current item chips + a set/unset status + a Select/Edit
 *    button that opens the picker;
 *  - the **item-picker modal** ({@link ItemPicker}) — search the catalog,
 *    tap-toggle items (`aria-pressed`), a selected bar, a count, Save → persists
 *    via {@link useSetCampaignScope}; dialog semantics + keyboard (Escape, Tab
 *    trap), consistent with the Step-2 MergePicker;
 *  - a Back / Skip-for-now / Continue-to-summary button row.
 *
 * Cross-platform (Req 3.5): the picker shows display names but persists the
 * platform's own `externalItemId` VERBATIM — a Talabat deal's ids are product
 * NAMES, a Careem deal's are numeric ids — so the matcher reconciles either.
 * `--font-num` for counts/prices; logical properties + bilingual-ready markup.
 */
function CampaignsStep({
  store,
  onBack,
  onNext,
}: {
  store: StoreResponse;
  onBack: () => void;
  onNext: () => void;
}) {
  const scope = useCampaignScope(store.id);
  const setScope = useSetCampaignScope(store.id);
  const currency = store.profile.currency;

  const body = scope.data?.status === 200 ? scope.data.body : undefined;
  const candidates: CampaignScopeCandidate[] = body?.candidates ?? [];
  const catalog: CatalogPickItem[] = body?.catalog ?? [];

  // The campaign whose picker is open (null = closed); held by id so a refreshed
  // read keeps the dialog pointed at the live candidate.
  const [pickerCampaignId, setPickerCampaignId] = useState<string | null>(null);
  const activeCandidate =
    pickerCampaignId === null
      ? undefined
      : candidates.find((c) => c.campaign.id === pickerCampaignId);

  // The polite outcome of the most recent save (announced to AT + shown as a
  // transient note), cleared when a new picker opens.
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const statusId = useId();

  const unsetCount = candidates.filter((c) => c.scopedProductIds.length === 0).length;
  const allSet = candidates.length > 0 && unsetCount === 0;

  function openPicker(campaignId: string) {
    setSaveStatus(null);
    setPickerCampaignId(campaignId);
  }
  function closePicker() {
    setPickerCampaignId(null);
  }

  function saveScope(campaign: Campaign, scopedProductIds: string[]) {
    setScope.mutate(
      { params: { id: store.id }, body: { campaignId: campaign.id, scopedProductIds } },
      {
        onSuccess: (res) => {
          if (res.status !== 200) return;
          closePicker();
          setSaveStatus(
            scopedProductIds.length === 0
              ? `${campaign.name}: items cleared — it’s a whole-order deal again.`
              : `${campaign.name}: ${scopedProductIds.length} item${
                  scopedProductIds.length === 1 ? '' : 's'
                } set.`,
          );
        },
      },
    );
  }

  return (
    <section className="card campaigns-step" aria-label="Campaigns — selected-items deals">
      <div className="campaigns-step__head">
        <h3>Which items do these deals apply to?</h3>
        <p className="muted">
          Some of your deals discount only selected items, not the whole order. The platforms don’t
          always tell us which items — so when we can’t, you do, once, here. We use it to attribute
          each deal’s discount to the right orders. Whole-menu deals aren’t shown; they need nothing.
        </p>
      </div>

      <QueryState isLoading={scope.isLoading} isError={scope.isError} label="your deals">
        {candidates.length === 0 ? (
          <p className="muted campaigns-step__empty" role="status">
            No selected-items deals to set up. Every deal we found applies to your whole menu, so
            there’s nothing to scope here — continue to the summary.
          </p>
        ) : (
          <>
            {/* Informed skip nudge (Req 3.4): counts the unset candidates, or an
                all-set confirmation when none remain. */}
            <p
              className="campaigns-nudge"
              data-state={allSet ? 'ok' : 'warn'}
              role="status"
              aria-live="polite"
            >
              <span className="campaigns-nudge__icon" aria-hidden="true">
                {allSet ? '✓' : 'i'}
              </span>
              {allSet ? (
                <span>
                  <b>All set.</b> Every selected-items deal has its items — their discounts will
                  attribute to the right orders.
                </span>
              ) : (
                <span>
                  <b>
                    <span className="num">{unsetCount}</span> deal{unsetCount === 1 ? '' : 's'} still
                    need{unsetCount === 1 ? 's' : ''} items.
                  </b>{' '}
                  You can skip — those deals will keep attributing on the whole-order discount (less
                  precise), and you can set them later from the campaign page.
                </span>
              )}
            </p>

            {saveStatus !== null && (
              <p id={statusId} className="campaigns-step__saved muted" role="status" aria-live="polite">
                {saveStatus}
              </p>
            )}

            <ul className="campaigns-deals" aria-label="Selected-items deals">
              {candidates.map((candidate) => (
                <CampaignDealRow
                  key={candidate.campaign.id}
                  candidate={candidate}
                  catalog={catalog}
                  currency={currency}
                  onSelect={() => openPicker(candidate.campaign.id)}
                />
              ))}
            </ul>
          </>
        )}
      </QueryState>

      <div className="wizard-actions campaigns-step__actions">
        <button type="button" onClick={onBack}>
          Back
        </button>
        <span className="campaigns-step__spacer" aria-hidden="true" />
        <button type="button" className="link campaigns-step__skip" onClick={onNext}>
          Skip for now
        </button>
        <button type="button" className="campaigns-step__continue" onClick={onNext}>
          Continue to summary
        </button>
      </div>

      {activeCandidate !== undefined && (
        <ItemPicker
          candidate={activeCandidate}
          catalog={catalog}
          currency={currency}
          pending={setScope.isPending}
          error={setScope.isError}
          onCancel={closePicker}
          onSave={(ids) => saveScope(activeCandidate.campaign, ids)}
        />
      )}
    </section>
  );
}

/**
 * One scoped-candidate deal row (the mock's `.deal` card): name + platform badge,
 * the synthesized terms line, the "why" line, current item chips (display names
 * resolved from the catalog, capped with a "+N" overflow chip), a set/unset
 * status, and a Select-items / Edit-items button. Chips resolve `externalItemId`
 * → display name from the catalog; an id with no catalog row (a stale scope)
 * falls back to the raw id so nothing is hidden.
 */
function CampaignDealRow({
  candidate,
  catalog,
  currency,
  onSelect,
}: {
  candidate: CampaignScopeCandidate;
  catalog: readonly CatalogPickItem[];
  currency: string | undefined;
  onSelect: () => void;
}) {
  const { campaign, reason, scopedProductIds } = candidate;
  const plat = CAMPAIGN_PLAT[campaign.platformKey] ?? CAMPAIGN_PLAT.talabat;
  const set = scopedProductIds.length > 0;
  const nameFor = (ext: string) => catalog.find((row) => row.externalItemId === ext)?.name ?? ext;
  const shownChips = scopedProductIds.slice(0, 6);
  const overflow = scopedProductIds.length - shownChips.length;

  return (
    <li className="campaigns-deal">
      <div className="campaigns-deal__main">
        <div className="campaigns-deal__name">
          <bdi>{campaign.name}</bdi>
          <span className={`campaigns-plat campaigns-plat--${plat.className}`}>{plat.label}</span>
        </div>
        {campaignTermsLine(campaign, currency) !== '' && (
          <div className="campaigns-deal__terms">{campaignTermsLine(campaign, currency)}</div>
        )}
        <div className="campaigns-deal__why">
          <span className="campaigns-deal__why-icon" aria-hidden="true">
            i
          </span>
          {candidateWhy(reason)}
        </div>
        {set && (
          <ul className="campaigns-deal__items" aria-label={`Items set for ${campaign.name}`}>
            {shownChips.map((ext) => (
              <li key={ext} className="campaigns-itemchip">
                <bdi>{nameFor(ext)}</bdi>
              </li>
            ))}
            {overflow > 0 && (
              <li className="campaigns-itemchip campaigns-itemchip--more">
                +<span className="num">{overflow}</span>
              </li>
            )}
          </ul>
        )}
      </div>
      <div className="campaigns-deal__act">
        <span className={`campaigns-status campaigns-status--${set ? 'set' : 'unset'}`}>
          <span className="campaigns-status__dot" aria-hidden="true" />
          {set ? (
            <>
              <span className="num">{scopedProductIds.length}</span> item
              {scopedProductIds.length === 1 ? '' : 's'} set
            </>
          ) : (
            'No items yet'
          )}
        </span>
        <button
          type="button"
          className={set ? 'campaigns-deal__edit' : 'campaigns-deal__select'}
          onClick={onSelect}
        >
          {set ? 'Edit items' : 'Select items'}
        </button>
      </div>
    </li>
  );
}

/**
 * The item-picker modal (the mock's `.scrim` + `.modal`): search the store's
 * catalog, tap-toggle items (`aria-pressed`), a selected bar with removable
 * chips, a live count, and Save. Dialog semantics + keyboard (Escape closes, Tab
 * is trapped), mirroring the Step-2 {@link MergePicker} a11y pattern.
 *
 * The draft selection is a `Set<string>` of `externalItemId`s, seeded from the
 * candidate's current scope; Save persists the draft VERBATIM (Req 1.2) — no id
 * transform — so a Talabat deal persists product names and a Careem deal numeric
 * ids. Catalog rows with no name still render (the raw id), and search filters
 * on the display name.
 */
function ItemPicker({
  candidate,
  catalog,
  currency,
  pending,
  error,
  onCancel,
  onSave,
}: {
  candidate: CampaignScopeCandidate;
  catalog: readonly CatalogPickItem[];
  currency: string | undefined;
  pending: boolean;
  error: boolean;
  onCancel: () => void;
  onSave: (scopedProductIds: string[]) => void;
}) {
  const { campaign } = candidate;
  const plat = CAMPAIGN_PLAT[campaign.platformKey] ?? CAMPAIGN_PLAT.talabat;
  const titleId = useId();
  const subId = useId();
  const errorId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<Set<string>>(() => new Set(candidate.scopedProductIds));

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const needle = query.trim().toLowerCase();
  const nameFor = (ext: string) =>
    catalog.find((row) => row.externalItemId === ext)?.name ?? ext;
  const shown =
    needle === ''
      ? catalog
      : catalog.filter((row) => row.name.toLowerCase().includes(needle));

  function toggle(ext: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(ext)) next.delete(ext);
      else next.add(ext);
      return next;
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (!pending) onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  const selected = [...draft];

  return createPortal(
    <div
      className="campaigns-picker__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !pending) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="campaigns-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subId}
        onKeyDown={onKeyDown}
      >
        <div className="campaigns-picker__head">
          <h3 id={titleId} className="campaigns-picker__title">
            Select items — <bdi>{campaign.name}</bdi>
          </h3>
          <p id={subId} className="campaigns-picker__sub muted">
            {plat.label} · pick the catalog items this deal discounts
          </p>
        </div>

        <div className="campaigns-picker__search-wrap">
          <input
            ref={searchRef}
            type="text"
            className="campaigns-picker__search"
            placeholder="Search the catalog…"
            aria-label="Search catalog items"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="campaigns-picker__selbar">
          {selected.length === 0 ? (
            <span className="campaigns-picker__selbar-label muted">
              No items selected yet — search and tap to add.
            </span>
          ) : (
            <>
              <span className="campaigns-picker__selbar-label muted">Selected:</span>
              {selected.map((ext) => (
                <span key={ext} className="campaigns-selchip">
                  <bdi>{nameFor(ext)}</bdi>
                  <button
                    type="button"
                    className="campaigns-selchip__remove"
                    aria-label={`Remove ${nameFor(ext)}`}
                    onClick={() => toggle(ext)}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </>
          )}
        </div>

        <div className="campaigns-picker__opts" role="group" aria-label="Catalog items">
          {shown.length === 0 ? (
            <p className="muted campaigns-picker__empty" role="status">
              No items match “{query}”.
            </p>
          ) : (
            shown.map((row) => {
              const on = draft.has(row.externalItemId);
              return (
                <button
                  key={row.externalItemId}
                  type="button"
                  className="campaigns-opt"
                  aria-pressed={on}
                  onClick={() => toggle(row.externalItemId)}
                >
                  <span className="campaigns-opt__check" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                  <span className="campaigns-opt__name">
                    <bdi>{row.name}</bdi>
                  </span>
                  <span className="campaigns-opt__price num">{money(row.basePrice, currency)}</span>
                </button>
              );
            })
          )}
        </div>

        {error && (
          <p id={errorId} className="error campaigns-picker__error" role="alert">
            Couldn’t save the items. Check your connection and try again.
          </p>
        )}

        <div className="campaigns-picker__foot">
          <span className="campaigns-picker__count">
            <b className="num">{draft.size}</b> item{draft.size === 1 ? '' : 's'} selected
          </span>
          <button type="button" onClick={onCancel} aria-disabled={pending || undefined}>
            Cancel
          </button>
          <button
            type="button"
            className="campaigns-picker__save"
            aria-disabled={pending || undefined}
            aria-describedby={error ? errorId : undefined}
            onClick={() => !pending && onSave(selected)}
          >
            {pending ? 'Saving…' : 'Save items'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Data_Gathering_Step — the leading gather (unified-data-gathering Req 3) ──

/**
 * The six sources the mock lists, mapped onto the server's five-key
 * derive-progress record (the gather writes the SAME derive-progress store,
 * task 7.2). The mock shows `orders / statements / payouts / ads&campaigns /
 * catalog items / catalog options`; the progress record keys are
 * `catalog / statements / detailed / orders / payouts`. "Detailed" is the
 * per-month detailed XLSX that backs orders, so it reads as part of orders here;
 * ads/campaigns and catalog-options have no own progress key (they ride the
 * catalog + orders pulls), so the checklist shows the five tracked keys and the
 * populated state surfaces the full per-source counts from the result body.
 */
const GATHER_SOURCE_LABEL: Record<DeriveSourceStatus['source'], string> = {
  catalog: 'Catalog items & options',
  statements: 'Statements',
  detailed: 'Detailed reports',
  orders: 'Orders',
  payouts: 'Payouts',
};

/** The number of gathered sources the mock headlines ("N of 6 sources"). */
const GATHER_SOURCE_COUNT = 6;

/** Whether a gather result finished cleanly (every reference source pulled). */
function gatherSucceeded(result: OnboardingGatherResult): boolean {
  return result.ok && result.failedSources.length === 0;
}

/**
 * The leading `Data_Gathering_Step` (unified-data-gathering Req 3.1, 3.5, 6.3,
 * 6.4, 8.3, 8.4) — the FIRST onboarding step, reproducing the signed-off mock
 * `brand/unified-data-gathering-mock.html` on the real design system. Four
 * states:
 *  - **empty** — "Ready to gather" + the reference-window chip + "Start
 *    gathering" (Req 3.1);
 *  - **loading** — the per-source checklist driven by {@link useDeriveProgress}
 *    (the gather writes the same derive-progress store, task 7.2) + an "N of 6
 *    sources saved" status (Req 3.2). No Cancel: the gather is a single
 *    server-tracked run (task 7.5) the client polls to completion, so there is
 *    no client-only "stop" that would leave the server run orphaned;
 *  - **error** — "Talabat session not connected" + Reconnect/Retry, explicitly
 *    NO upload fallback (Req 3.5);
 *  - **populated** — "All sources gathered and saved" + "Continue to food cost"
 *    (advances to Step 1).
 *
 * RESUME-INTO-PROGRESS (Req 8.3, 8.4): if the store already has a gather
 * `running` (`store.gatherRunning`, server-derived from `ingestion_runs`), or
 * the mutation returned `alreadyRunning`, the step renders the LOADING state and
 * polls the progress until it finalizes — never the empty "start gathering"
 * page while a gather is in flight. The gather dedups server-side (task 7.5), so
 * a reload / second tab attaches to the one running run.
 *
 * Copy is inline English to match the surrounding Step1/Step2/Step3 convention
 * (they use inline English, not i18n keys).
 */
function GatherStep({ store, onNext }: { store: StoreResponse; onNext: () => void }) {
  const gather = useGather(store.id);
  const queryClient = tsr.useQueryClient();
  // Own-platform display name (mythings-breakdown-reconciliation Req 1).
  const { platformLabel } = platformLabels(store.platformKey);

  // ts-rest surfaces the always-200 gather body on `data`; a failed/incomplete
  // gather is reported IN the body (`ok:false` + `failedSources`), never an HTTP
  // error, so the UI offers Retry without an upload fallback (Req 3.5).
  const result: OnboardingGatherResult | undefined =
    gather.data?.status === 200 ? gather.data.body : undefined;

  // RESUME (Req 8.3): the store read carries `gatherRunning` (server-derived from
  // `ingestion_runs`). A gather is "in flight" when this request is running, OR
  // the store reports a running gather, OR our POST deduped onto an existing run
  // (`alreadyRunning`). `attached` is the pre-poll notion of "a server gather we
  // follow"; the authoritative FINISH comes from the progress poll below.
  const attached = gather.isPending || store.gatherRunning || !!result?.alreadyRunning;

  // Keep polling the progress store while attached OR while we've seen a run that
  // hasn't reported finished yet — so a long/dropped gather mutation can't stop
  // the poll before it observes the terminal `running:false` (the hang bug).
  const [keepPolling, setKeepPolling] = useState(false);
  const progressQuery = useDeriveProgress(store.id, attached || keepPolling);
  const progress: DeriveProgress | undefined =
    progressQuery.data?.status === 200 ? progressQuery.data.body : undefined;
  const progressRunning = progress?.running ?? null;

  // Track that the poll has OBSERVED a running gather, so a later `running:false`
  // is a real finish (not an empty/never-started record).
  const sawProgressRunning = useRef(false);
  useEffect(() => {
    if (progressRunning === true) {
      sawProgressRunning.current = true;
      setKeepPolling(true);
    } else if (progressRunning === false) {
      setKeepPolling(false);
    }
  }, [progressRunning]);

  // The progress poll is the authoritative terminal signal: once it reports the
  // gather finished, a stale `alreadyRunning` / unrefreshed `gatherRunning` must
  // NOT keep us "running" (that caused the hang-until-refresh).
  const progressFinished = progressRunning === false && sawProgressRunning.current;
  const resumeRunning = store.gatherRunning || !!result?.alreadyRunning;
  const running = (gather.isPending || resumeRunning) && !progressFinished;

  // A finished-but-failed result (session not connected, etc.) → the error state.
  const failed = result !== undefined && !result.alreadyRunning && !gatherSucceeded(result);

  // RESUME-FINALIZE (Req 8.4): when the poll reports the gather finished, re-read
  // the store so its server-derived `gatherRunning`/`referenceDataPresent`
  // refresh and drive the advance effect below.
  useEffect(() => {
    // Re-read the store the moment the poll reports the gather finished — whether
    // we came in via resume OR our own mutation (which may still be awaiting a
    // slow HTTP response). The refreshed `gatherRunning`/`referenceDataPresent`
    // then drive the advance effect below.
    if (progressRunning === false && (resumeRunning || sawProgressRunning.current)) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.store(store.id) });
    }
  }, [resumeRunning, progressRunning, queryClient, store.id]);

  // Remember that we were resuming a run so we can distinguish a finalized
  // resume (advance) from a never-started gather (stay on the empty page).
  const wasResuming = useRef(false);
  useEffect(() => {
    if (resumeRunning) wasResuming.current = true;
  }, [resumeRunning]);


  // When a gather finalizes cleanly, advance to Step 1 (Req 8.4):
  //  - our own mutation returned a clean result; OR
  //  - (resume path) the store we were resuming cleared `gatherRunning` and now
  //    has reference data — the run finished successfully server-side.
  // A finalized resume with NO reference data means it failed; the store read's
  // next gather attempt surfaces the error, so we don't auto-advance there.
  useEffect(() => {
    if (running) return;
    if (result && gatherSucceeded(result)) {
      onNext();
      return;
    }
    // Resume path OR own-mutation-with-slow-response: the progress poll saw the
    // gather run then finish, and the refreshed store shows it landed reference
    // data and is no longer running → advance without waiting on the mutation.
    if (
      (wasResuming.current || sawProgressRunning.current) &&
      !store.gatherRunning &&
      store.referenceDataPresent
    ) {
      onNext();
    }
  }, [running, result, onNext, store.gatherRunning, store.referenceDataPresent]);

  function start() {
    gather.mutate({ params: { id: store.id }, body: {} });
  }

  // ── error state (Req 3.5) ──
  if (failed) {
    return (
      <section className="card" aria-label="Gather data">
        <h3>Gathering your data</h3>
        <div role="alert" className="notice">
          <p className="error">
            {platformLabel} session not connected. We couldn&apos;t reach your {platformLabel} session,
            so gathering stopped. Reconnect the session in your Chrome, then retry — Clarity never falls
            back to a file upload.
          </p>
          {result && result.failedSources.length > 0 && (
            <p className="muted">Failed sources: {result.failedSources.join(', ')}.</p>
          )}
          <div className="wizard-actions">
            <button type="button" onClick={start} disabled={gather.isPending}>
              {gather.isPending ? 'Retrying…' : 'Retry gathering'}
            </button>
          </div>
          <p className="muted">
            Reconnect the session in Chrome, then retry. There is no file-upload fallback.
          </p>
        </div>
      </section>
    );
  }

  // ── loading state (Req 3.2, 8.3) ──
  if (running) {
    const sources = progress?.sources ?? [];
    const done = sources.filter((s) => s.state === 'done' || s.state === 'skipped').length;
    return (
      <section className="card" aria-label="Gather data">
        <h3>Gathering your data</h3>
        <p className="muted">
          Pulling your last 6 months from {platformLabel} once and saving it. Later steps read this
          saved snapshot, so nothing is fetched twice.
        </p>
        <GatherProgressChecklist progress={progress} />
        <p className="muted" role="status" aria-live="polite">
          <span className="num">{done}</span> of {GATHER_SOURCE_COUNT} sources saved
        </p>
      </section>
    );
  }

  // ── populated state — a clean finished gather (advance handled in the effect) ──
  if (result && gatherSucceeded(result)) {
    const p = result.persisted;
    return (
      <section className="card" aria-label="Gather data">
        <h3>Gathering your data</h3>
        <p role="status" className="success">
          All sources gathered and saved.
        </p>
        <p className="muted">
          Saved <span className="num">{p.orders}</span> orders,{' '}
          <span className="num">{p.statements}</span> statements,{' '}
          <span className="num">{p.payouts}</span> payouts,{' '}
          <span className="num">{p.campaigns}</span> campaigns and{' '}
          <span className="num">{p.catalogItems}</span> catalog items.
        </p>
        <div className="wizard-actions">
          <button type="button" onClick={onNext}>
            Continue to food cost
          </button>
        </div>
      </section>
    );
  }

  // ── empty state (Req 3.1) ──
  return (
    <section className="card" aria-label="Gather data">
      <h3>Ready to gather</h3>
      <p className="muted">
        Clarity will pull your last 6 months from {platformLabel} once and save it. The next steps read
        what we save here, so your review never waits on a live fetch.
      </p>
      <p className="muted">Reference window: 6 completed months.</p>
      <div className="wizard-actions">
        <button type="button" onClick={start} disabled={gather.isPending}>
          Start gathering
        </button>
      </div>
    </section>
  );
}

/**
 * The teal-glass per-source checklist shown while a gather is in flight — the
 * same live {@link DeriveProgress} record the Step-3 derive checklist reads (the
 * gather writes it too, task 7.2), rendered with the same design-system
 * primitives. Before the first event lands it shows a neutral "Starting…".
 */
function GatherProgressChecklist({ progress }: { progress: DeriveProgress | undefined }) {
  const sources = progress?.sources ?? [];
  return (
    <div
      className="derive-progress"
      role="status"
      aria-live="polite"
      aria-label="Gathering — live progress"
    >
      <p className="derive-progress__title">Gathering…</p>
      {sources.length === 0 ? (
        <p className="muted">Starting…</p>
      ) : (
        <ul className="derive-progress__list">
          {sources.map((s) => (
            <li key={s.source} className="derive-progress__row" data-state={s.state}>
              <span className="derive-progress__glyph" aria-hidden="true">
                {stateGlyph(s.state)}
              </span>
              <span className="derive-progress__label">{GATHER_SOURCE_LABEL[s.source]}</span>
              <SourceCount s={s} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Step 1 — food cost + markup + payment-gateway params ────────────────────

function Step1({ store, onNext }: { store: StoreResponse; onNext: () => void }) {
  const step1 = useOnboardingStep1(store.id);
  const profile = store.profile;

  // The admin-set per-platform fee defaults (onboarding Req 4.1). The fee inputs
  // seed from the store's OWN value when set, otherwise from the matching
  // platform default — so a fresh store shows the inherited defaults to confirm
  // or override, instead of blank fields. The store value always wins.
  const feeDefaults = usePlatformFeeDefaults();
  const platformDefault =
    feeDefaults.data?.status === 200
      ? feeDefaults.data.body.find((d) => d.platformKey === store.platformKey)
      : undefined;
  const feeDefaultsReady = feeDefaults.data !== undefined;

  const [markupPct, setMarkupPct] = useState(String(profile.markup_pct ?? 0));
  const [defaultFoodCostPct, setDefaultFoodCostPct] = useState(
    String(profile.default_food_cost_pct ?? 30),
  );
  const [payFeePct, setPayFeePct] = useState(String(profile.pay_fee_pct ?? ''));
  const [payFeeFixed, setPayFeeFixed] = useState(String(profile.pay_fee_fixed ?? ''));
  const [payFeeTaxPct, setPayFeeTaxPct] = useState(String(profile.pay_fee_tax_pct ?? ''));

  // Once the platform defaults arrive, seed any fee field the store hasn't set
  // yet from the matching default (user edits already made are preserved — we
  // only fill a field that is still blank and whose store value is unset).
  useEffect(() => {
    if (!feeDefaultsReady || !platformDefault) return;
    if (profile.pay_fee_pct === undefined && platformDefault.pay_fee_pct !== undefined) {
      setPayFeePct((v) => (v.trim() === '' ? String(platformDefault.pay_fee_pct) : v));
    }
    if (profile.pay_fee_fixed === undefined && platformDefault.pay_fee_fixed !== undefined) {
      setPayFeeFixed((v) => (v.trim() === '' ? String(platformDefault.pay_fee_fixed) : v));
    }
    if (profile.pay_fee_tax_pct === undefined && platformDefault.pay_fee_tax_pct !== undefined) {
      setPayFeeTaxPct((v) => (v.trim() === '' ? String(platformDefault.pay_fee_tax_pct) : v));
    }
  }, [
    feeDefaultsReady,
    platformDefault,
    profile.pay_fee_pct,
    profile.pay_fee_fixed,
    profile.pay_fee_tax_pct,
  ]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const body = {
      markup_pct: Number(markupPct),
      default_food_cost_pct: Number(defaultFoodCostPct),
      ...(payFeePct.trim() === '' ? {} : { pay_fee_pct: Number(payFeePct) }),
      ...(payFeeFixed.trim() === '' ? {} : { pay_fee_fixed: Number(payFeeFixed) }),
      ...(payFeeTaxPct.trim() === '' ? {} : { pay_fee_tax_pct: Number(payFeeTaxPct) }),
    };
    step1.mutate(
      { params: { id: store.id }, body },
      {
        onSuccess: (res) => {
          if (res.status === 200) onNext();
        },
      },
    );
  }

  return (
    <form className="card" onSubmit={onSubmit} aria-label="Step 1 — food cost and markup">
      <h3>Step 1 — food cost &amp; markup</h3>
      <label>
        Default food cost %
        <input
          type="number"
          min={0}
          max={100}
          step="any"
          value={defaultFoodCostPct}
          onChange={(e) => setDefaultFoodCostPct(e.target.value)}
          required
        />
      </label>
      <label>
        Markup %
        <input
          type="number"
          min={0}
          max={100}
          step="any"
          value={markupPct}
          onChange={(e) => setMarkupPct(e.target.value)}
          required
        />
      </label>

      <fieldset>
        <legend>Payment-gateway params (confirm or override)</legend>
        {platformDefault &&
          (platformDefault.pay_fee_pct !== undefined ||
            platformDefault.pay_fee_fixed !== undefined ||
            platformDefault.pay_fee_tax_pct !== undefined) && (
            <p className="muted">
              Prefilled from the platform defaults. Edit to override for this store.
            </p>
          )}
        <label>
          Fee % (variable)
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            value={payFeePct}
            onChange={(e) => setPayFeePct(e.target.value)}
          />
        </label>
        <label>
          Fee fixed (per order)
          <input
            type="number"
            min={0}
            step="any"
            value={payFeeFixed}
            onChange={(e) => setPayFeeFixed(e.target.value)}
          />
        </label>
        <label>
          Fee tax %
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            value={payFeeTaxPct}
            onChange={(e) => setPayFeeTaxPct(e.target.value)}
          />
        </label>
      </fieldset>

      <button type="submit" disabled={step1.isPending}>
        {step1.isPending ? 'Saving…' : 'Save & continue'}
      </button>
      {step1.isError && (
        <p role="alert" className="error">
          Could not save step 1.
        </p>
      )}
    </form>
  );
}

// ─── Step 2 — catalog review (per-item edit-or-confirm) ──────────────────────

/**
 * Step 2 — the catalog-review surface (onboarding Req 2.2). EXPORTED so the
 * clarity-navigation-scope **Catalog** surface can reuse the exact same
 * catalog-review UI against a scope's single store without duplicating any
 * catalog logic (nav-scope task 5.3, Req 4.1).
 *
 * EXTRACTION NOTE (task 5.3): the task sanctions "if extraction is non-trivial,
 * ship a thin route rendering the existing component". A full clean lift of the
 * review body into a store-only component is non-trivial — this function owns a
 * large web of local closures (per-item/-option drafts, in-place confirm, merge/
 * unmerge, back-date editors, Items/Options tabs) all over the same `store`. So
 * rather than extract, we EXPORT `Step2` and render it directly for the scope's
 * store in `CatalogSurface` (see `WorkspaceSurface.tsx`). No catalog math moves.
 *
 * The only concession to standalone use is CHROME, not logic: in the wizard,
 * `onBack`/`onNext` drive the step cursor; as a standalone Catalog surface there
 * is no step 2→3 flow, so both are omitted. When they're absent the footer drops
 * the wizard "Back" button and the submit relabels from "Confirm catalog &
 * continue" to "Save catalog" — the SAME `saveCatalog` persist path either way
 * (edits + option edits), so there is no behavioural or math change.
 */
export function Step2({
  store,
  onBack,
  onNext,
}: {
  store: StoreResponse;
  /** Wizard step-back. Omitted when rendered as the standalone Catalog surface. */
  onBack?: () => void;
  /** Advance to step 3 after a successful save. Omitted standalone (no next step). */
  onNext?: () => void;
}) {
  // Own-platform display name (mythings-breakdown-reconciliation Req 1).
  const { platformLabel } = platformLabels(store.platformKey);
  const catalog = useOnboardingCatalog(store.id);
  const saveCatalog = useSaveOnboardingCatalog(store.id);
  // A SEPARATE instance of the same save for the in-place confirms (per row +
  // confirm-all; catalog-coverage Req 6.3, 6.4): its own pending/error state, so
  // confirming one item doesn't flip the main button to "Saving…" or navigate.
  const confirmItems = useSaveOnboardingCatalog(store.id);
  // One write at a time: `saveCatalog` re-versions the whole catalog, so two
  // overlapping saves could each read the old set and drop the other's flip.
  // The merge route re-versions two catalog items, so it joins the same lock.
  // Removing an alias (unmerge) re-versions two items as well, so it joins too.
  const merge = useMergeCatalog(store.id);
  const unmerge = useUnmergeCatalog(store.id);
  const saving =
    saveCatalog.isPending || confirmItems.isPending || merge.isPending || unmerge.isPending;

  const review = catalog.data?.status === 200 ? catalog.data.body : undefined;
  const items: CatalogReviewItem[] = review?.items ?? [];
  const markupPct = review?.markup_pct ?? store.profile.markup_pct ?? 0;

  // Connection state from the review. Now that Step 2 is a pure read of the
  // persisted catalog (unified-data-gathering Req 4.1–4.3), the server always
  // reports `connected` — reading persisted rows needs no acquisition session —
  // so `notConnected` stays false. The empty-but-not-connected guard is kept
  // against the contract field for safety; it no longer gates a re-pull.
  const connection = review?.connection ?? 'connected';
  const notConnected = items.length === 0 && connection !== 'connected';

  // Step 2 is a PURE READ now (unified-data-gathering Req 4.1, 4.2): the catalog
  // — menu items, synced options, and orders-driven provisioned items alike —
  // was pulled and persisted UP FRONT by the leading `Data_Gathering_Step`, so
  // this surface no longer triggers a first-visit orders-provisioning pull. The
  // provisioned-from-orders group still RENDERS from the persisted catalog (an
  // `archived` item is one seen in orders, absent from the menu); it is simply
  // read, not fetched. Edits below remain WRITES (effective-dated versions).

  // The id of the item whose back-dated food-cost editor is open (Req 11.4–11.6),
  // or null when none is expanded.
  const [backdateItemId, setBackdateItemId] = useState<string | null>(null);
  // The result of the most recent back-dated edit, held in the (stable) Step 2
  // component so a rejected effective date (409) surfaces even if the inline
  // editor subtree re-renders when the catalog cache settles.
  const [backdateResult, setBackdateResult] = useState<
    { itemId: string; conflict: string | null; failed: boolean } | null
  >(null);

  // Per-item working edits keyed by catalog item id. Seeded from the review.
  // `foodCostValue` is an OPTIONAL raw draft of the absolute food-cost input: it
  // holds the exact keystrokes while the value field is being typed (so decimals
  // aren't reformatted mid-entry); when absent, the value column is derived from
  // `foodCostPct × basePrice`. Only `foodCostPct` + `basePrice` are saved.
  const [edits, setEdits] = useState<
    Record<string, { foodCostPct: string; basePrice: string; foodCostValue?: string }>
  >({});

  useEffect(() => {
    if (!review) return;
    setEdits((prev) => {
      // Only seed items we have not already touched, so a re-fetch keeps user edits.
      const next = { ...prev };
      for (const row of review.items) {
        if (!(row.item.id in next)) {
          next[row.item.id] = {
            foodCostPct: String(row.item.foodCostPct),
            basePrice: String(row.item.basePrice),
          };
        }
      }
      return next;
    });
  }, [review]);

  // Per-option-row drafts keyed by `optionKey` (clarity-catalog-options Req 9.8),
  // held here — above both tab panels — so a tab switch keeps them. UNSEEDED: a
  // row gets an entry only once a field is typed in, and each field only once
  // touched, so "changed rows only" (the main submit) is a draft-vs-row compare.
  const [optionDrafts, setOptionDrafts] = useState<Record<string, OptionDraft>>({});

  // A row's `optionKey` embeds its rounded price, so a saved price change (or a
  // fan-out that merges rows) retires the old key: drop drafts for keys the
  // refreshed review no longer carries, so a stale draft never resurfaces.
  useEffect(() => {
    if (!review) return;
    const live = new Set(review.options.map((row) => row.optionKey));
    setOptionDrafts((prev) => {
      const kept = Object.entries(prev).filter(([key]) => live.has(key));
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    });
  }, [review]);

  function setOptionField(optionKey: string, field: keyof OptionDraft, value: string) {
    setOptionDrafts((prev) => ({ ...prev, [optionKey]: { ...prev[optionKey], [field]: value } }));
  }

  /**
   * The values an option row's inputs show: the draft when typed, else the row's
   * own. The % input is blank unless the row carries an override (blank ⇔
   * inherit), so an inherited / mixed row shows its rate as the placeholder.
   * An add-on with NO price yet (`unset`) shows a blank price, never a `0` that
   * reads like a real free add-on — Confirm then requires a value.
   */
  function optionDraftValues(row: CatalogOptionRow): Required<OptionDraft> {
    const draft = optionDrafts[row.optionKey];
    return {
      priceDelta: draft?.priceDelta ?? (isUnpricedOption(row) ? '' : String(row.priceDelta)),
      foodCostPct:
        draft?.foodCostPct ??
        (row.rateState === 'override' && row.foodCostPct !== null ? String(row.foodCostPct) : ''),
    };
  }

  /**
   * The row's current draft as an option fan-out edit, or `null` when the draft
   * isn't usable (blank / non-numeric / negative price, % outside 0–100). A blank
   * % means "inherit": it clears an override row's rate (`null`) and leaves an
   * inherited or mixed row's rates untouched (omitted).
   */
  function optionEditFor(row: CatalogOptionRow, confirm: boolean): CatalogOptionEdit | null {
    const draft = optionDraftValues(row);
    if (draft.priceDelta.trim() === '') return null;
    const priceDelta = Number(draft.priceDelta);
    if (!Number.isFinite(priceDelta) || priceDelta < 0) return null;
    const edit: CatalogOptionEdit = { optionKey: row.optionKey, priceDelta };
    if (draft.foodCostPct.trim() === '') {
      if (row.rateState === 'override') edit.foodCostPct = null;
    } else {
      const foodCostPct = Number(draft.foodCostPct);
      if (!Number.isFinite(foodCostPct) || foodCostPct < 0 || foodCostPct > 100) return null;
      edit.foodCostPct = foodCostPct;
    }
    if (confirm) edit.confirm = true;
    return edit;
  }

  function setField(itemId: string, field: 'foodCostPct' | 'basePrice', value: string) {
    setEdits((prev) => {
      const row = prev[itemId];
      // Editing the % (or base) directly drops any raw value-draft (by omitting
      // `foodCostValue`), so the value column resyncs to the derived amount
      // instead of a stale keystroke buffer.
      return {
        ...prev,
        [itemId]: {
          foodCostPct: row?.foodCostPct ?? '',
          basePrice: row?.basePrice ?? '',
          [field]: value,
        },
      };
    });
  }

  const currency = store.profile.currency;

  function platformPriceFor(basePrice: string): number {
    const base = Number(basePrice);
    if (Number.isNaN(base)) return 0;
    return base * (1 + markupPct / 100);
  }

  /** The absolute food-cost amount: pre-markup base × food-cost % (the stored rate). */
  function foodCostValueFor(foodCostPct: string, basePrice: string): number {
    const p = Number(foodCostPct);
    const base = Number(basePrice);
    if (Number.isNaN(p) || Number.isNaN(base)) return 0;
    return (base * p) / 100;
  }

  /**
   * Set the food cost from an absolute value by converting it back to the stored
   * percentage: `pct = value / base × 100`. Food cost is stored as a percentage
   * on the pre-markup base (Req 3.3), so the value column is a convenience input
   * that writes through to the canonical `foodCostPct`. The raw keystrokes are
   * kept in `foodCostValue` so typing a decimal isn't reformatted mid-entry; a
   * blank value clears the % too, and a zero/absent base leaves the pct as-is
   * (no rate derivable) while still showing the typed draft.
   */
  function setFoodCostValue(itemId: string, basePrice: string, value: string) {
    setEdits((prev) => {
      const row = prev[itemId] ?? { foodCostPct: '', basePrice: '' };
      const base = Number(basePrice);
      const amount = Number(value);
      let foodCostPct = row.foodCostPct;
      if (value.trim() === '') {
        foodCostPct = '';
      } else if (!Number.isNaN(amount) && !Number.isNaN(base) && base > 0) {
        // Round to a sane precision so the % field doesn't show float noise.
        foodCostPct = String(Math.round((amount / base) * 100 * 1e4) / 1e4);
      }
      return {
        ...prev,
        [itemId]: { ...row, basePrice: row.basePrice, foodCostPct, foodCostValue: value },
      };
    });
  }

  /**
   * The main-submit edit for one option row (Req 7.1, 7.2, 9.7): `undefined`
   * when the row has no draft or the draft matches the row (typed then
   * reverted), `null` when a changed draft isn't usable, else a MINIMAL edit —
   * `priceDelta` only when the price changed, `foodCostPct` only when the %
   * changed (`null` = clear an override row's rate). Minimal so the fan-out never
   * rewrites a field the owner didn't touch on any linked option.
   */
  function changedOptionEdit(row: CatalogOptionRow): CatalogOptionEdit | null | undefined {
    if (!(row.optionKey in optionDrafts)) return undefined;
    const draft = optionDraftValues(row);
    // A blank price is a change (and invalid): `Number('')` would read as 0.
    const priceChanged =
      draft.priceDelta.trim() === '' || Number(draft.priceDelta) !== row.priceDelta;
    const shownPct =
      row.rateState === 'override' && row.foodCostPct !== null ? String(row.foodCostPct) : '';
    const pctRaw = draft.foodCostPct.trim();
    const pctChanged =
      pctRaw === '' || shownPct === '' ? pctRaw !== shownPct : Number(pctRaw) !== Number(shownPct);
    if (!priceChanged && !pctChanged) return undefined;
    const full = optionEditFor(row, false);
    if (!full) return null;
    const edit: CatalogOptionEdit = { optionKey: row.optionKey };
    if (priceChanged) edit.priceDelta = full.priceDelta;
    if (pctChanged) edit.foodCostPct = full.foodCostPct;
    return edit;
  }

  function onSave(e: FormEvent) {
    e.preventDefault();
    const payload: CatalogEdit[] = items.map((row) => ({
      itemId: row.item.id,
      foodCostPct: Number(edits[row.item.id]?.foodCostPct ?? row.item.foodCostPct),
      basePrice: Number(edits[row.item.id]?.basePrice ?? row.item.basePrice),
    }));
    // Changed option rows only (Req 9.7); an unusable changed draft blocks the
    // whole save so a half-typed add-on price is never fanned out.
    const optionEdits: CatalogOptionEdit[] = [];
    const invalid: string[] = [];
    for (const row of optionRows) {
      const edit = changedOptionEdit(row);
      if (edit === null) invalid.push(row.name);
      else if (edit) optionEdits.push(edit);
    }
    if (invalid.length > 0) {
      setCatalogTab('options');
      setSubmitError(
        <>
          Fix the add-on {invalid.length === 1 ? 'draft' : 'drafts'} on the Options tab before
          continuing: enter a price and a food cost % from 0 to 100 for{' '}
          {invalid.map((name, i) => (
            <Fragment key={i}>
              {i > 0 && ', '}
              <bdi>{name}</bdi>
            </Fragment>
          ))}
          .
        </>,
      );
      return;
    }
    setSubmitError(null);
    // `optionEdits` is omitted when empty (the contract defaults it to `[]`).
    const body = optionEdits.length > 0 ? { edits: payload, optionEdits } : { edits: payload };
    saveCatalog.mutate(
      { params: { id: store.id }, body },
      {
        onSuccess: (res) => {
          // Standalone Catalog surface has no next step: the save alone is the
          // outcome (the catalog is re-persisted); only the wizard advances.
          if (res.status === 200) onNext?.();
        },
      },
    );
  }

  // Split the review into confirmed menu items and the auto-provisioned group
  // (catalog-coverage Req 6.2): `archived` ⇔ seen in orders, absent from the
  // menu. Display-only — `onSave` still sends EVERY item from both groups.
  const confirmedRows = items.filter((row) => row.item.status !== 'archived');
  const provisionedRows = items.filter((row) => row.item.status === 'archived');
  // The server's "needs a price" flag (estimate / unset base) — a subset of the
  // group: a `menu`/`line-price` provisioned item is still unreviewed but priced.
  const needsPriceCount = items.filter((row) => row.needsPrice).length;
  // The Options view rows (clarity-catalog-options Req 6.8) and their own
  // needs-a-price count for the Options tab badge (Req 9.3).
  const optionRows: CatalogOptionRow[] = review?.options ?? [];
  const optionsNeedPriceCount = optionRows.filter((row) => row.needsPrice).length;
  // The selected Items / Options tab (Req 9.1). Drafts (`edits`) live in this
  // component, above both panels, so switching tabs keeps them (Req 9.8).
  const [catalogTab, setCatalogTab] = useState<CatalogTabKey>('items');
  // A main-submit validation error (unusable changed add-on drafts), shown as an
  // alert beside the submit; cleared by the next submit that validates.
  const [submitError, setSubmitError] = useState<ReactNode>(null);
  const groupHeadingId = useId();
  const groupHelpId = useId();
  const pendingNoteId = useId();

  // ── In-place confirm (catalog-coverage Req 6.3, 6.4) ──
  // The ids currently being confirmed (drives each row's "Saving…" state), the
  // latest outcome for the polite live region, and the focus hand-off: a
  // confirmed row LEAVES the group once the refreshed review lands, so focus
  // moves to the next row's Confirm (else the previous, else the main submit)
  // instead of falling back to <body>.
  //
  // Generalised over items and option rows (clarity-catalog-options Req 9.6,
  // 9.9): one in-flight confirm at a time (`kind` + the item ids / option keys),
  // the SAME live region, and a per-kind focus hand-off. Confirm buttons are
  // registered as `${kind}:${id}` so an item id and an option key never collide.
  const [confirming, setConfirming] = useState<{
    kind: ConfirmKind;
    ids: readonly string[];
  } | null>(null);
  const isConfirming = (kind: ConfirmKind, id: string) =>
    confirming?.kind === kind && confirming.ids.includes(id);
  const confirmingMany = (kind: ConfirmKind) =>
    confirming?.kind === kind && confirming.ids.length > 1;
  const [confirmStatus, setConfirmStatus] = useState<{
    kind: 'ok' | 'error';
    // A node, not a string: item names (often Arabic on Talabat Jordan) are
    // wrapped in <bdi> so their direction can't reorder the sentence around them.
    message: ReactNode;
  } | null>(null);
  const confirmButtons = useRef(new Map<string, HTMLButtonElement>());
  const registerConfirm = (kind: ConfirmKind, id: string) => (el: HTMLButtonElement | null) => {
    if (el) confirmButtons.current.set(`${kind}:${id}`, el);
    else confirmButtons.current.delete(`${kind}:${id}`);
  };
  const submitRef = useRef<HTMLButtonElement>(null);

  // ── Merge into… (catalog-item-aliases Req 8.1–8.4, 8.8, 8.9) ──
  // The id of the source row whose picker is open (null = closed). The dialog's
  // content derives from the live review, so a refetch can't leave it stale.
  // Focus returns to the trigger on cancel; after a merge the source row is
  // gone, so it goes to the NEXT row's "Merge into…" in the same group (else the
  // previous, else the canonical row's) — i.e. where the owner was working.
  // Never to the canonical row by default: it can sit far away (a provisioned
  // item merged into a menu item at the top), and focusing it scrolls the page
  // there, losing the owner's place in a long list. The
  // conflict message is kept here too: the live region sits behind the modal.
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const mergeButtons = useRef(new Map<string, HTMLButtonElement>());
  const mergeReturnFocus = useRef<string | null>(null);
  const registerMerge = (id: string) => (el: HTMLButtonElement | null) => {
    if (el) mergeButtons.current.set(id, el);
    else mergeButtons.current.delete(id);
  };
  useEffect(() => {
    if (mergeSourceId !== null) return;
    const id = mergeReturnFocus.current;
    if (id === null) return;
    mergeReturnFocus.current = null;
    // A lone remaining row's "Merge into…" is disabled (nothing to merge into),
    // and a disabled button cannot take focus: fall back to the main submit.
    const button = mergeButtons.current.get(id);
    (button && !button.disabled ? button : submitRef.current)?.focus();
  }, [mergeSourceId]);
  const mergeSource =
    mergeSourceId === null ? undefined : items.find((row) => row.item.id === mergeSourceId)?.item;

  function openMerge(itemId: string) {
    mergeReturnFocus.current = itemId;
    setMergeError(null);
    setConfirmStatus(null);
    setMergeSourceId(itemId);
  }

  function closeMerge() {
    if (merge.isPending) return;
    setMergeError(null);
    setMergeSourceId(null);
  }

  function confirmMerge(source: CatalogReviewItem['item'], target: CatalogReviewItem['item']) {
    if (merge.isPending) return;
    // Resume point, chosen BEFORE the refetch drops the source row: its
    // neighbour in the group it sits in (the same split the tables render).
    const group = source.status === 'archived' ? provisionedRows : confirmedRows;
    const at = group.findIndex((row) => row.item.id === source.id);
    const resumeId = (group[at + 1] ?? group[at - 1])?.item.id ?? target.id;
    setMergeError(null);
    setConfirmStatus(null);
    const conflict = (message: string) => {
      // Keep the picker open and the source row in place (Req 8.8).
      setMergeError(message);
      setConfirmStatus({ kind: 'error', message });
    };
    const failed = () => {
      const message = `Couldn’t merge ${source.name} into ${target.name}. Check your connection and try again.`;
      setMergeError(message);
      setConfirmStatus({
        kind: 'error',
        message: (
          <>
            Couldn’t merge <bdi>{source.name}</bdi> into <bdi>{target.name}</bdi>. Check your
            connection and try again.
          </>
        ),
      });
    };
    merge.mutate(
      { params: { id: store.id }, body: { sourceItemId: source.id, targetItemId: target.id } },
      {
        // ts-rest may carry a non-2xx on the success channel OR reject it onto
        // onError — handle both, as the back-date editor does.
        onSuccess: (res) => {
          if (res.status === 200) {
            mergeReturnFocus.current = resumeId;
            setMergeError(null);
            setMergeSourceId(null);
            setConfirmStatus({
              kind: 'ok',
              message: (
                <>
                  <bdi>{source.name}</bdi> merged into <bdi>{target.name}</bdi>. Its name is now an
                  alias of <bdi>{target.name}</bdi>.
                </>
              ),
            });
          } else if (res.status === 409) conflict(errorMessage(res) ?? MERGE_CONFLICT_FALLBACK);
          else failed();
        },
        onError: (err) => {
          if (errorStatus(err) === 409) conflict(errorMessage(err) ?? MERGE_CONFLICT_FALLBACK);
          else failed();
        },
      },
    );
  }

  // ── Remove alias / unmerge (catalog-item-aliases Req 8.5–8.7, 8.9) ──
  // The outcome goes to the same polite live region as confirms and merges. A
  // removed alias's button leaves the DOM once the refreshed review lands, so
  // focus is handed on first (see the effect below) to the next alias's Remove
  // (else the previous, else the owner row's "Merge into…") instead of <body>.
  const aliasButtons = useRef(new Map<string, HTMLButtonElement>());
  const aliasFocusAfter = useRef<{ itemId: string; alias: string; next: string | null } | null>(
    null,
  );
  const aliasButtonKey = (itemId: string, alias: string) => JSON.stringify([itemId, alias]);
  const registerAlias = (itemId: string, alias: string) => (el: HTMLButtonElement | null) => {
    if (el) aliasButtons.current.set(aliasButtonKey(itemId, alias), el);
    else aliasButtons.current.delete(aliasButtonKey(itemId, alias));
  };
  const aliasesKey = JSON.stringify(items.map((row) => [row.item.id, row.item.aliases]));
  useEffect(() => {
    const pending = aliasFocusAfter.current;
    if (!pending) return;
    const current = JSON.parse(aliasesKey) as [string, string[]][];
    const owned = current.find(([id]) => id === pending.itemId)?.[1] ?? [];
    // Still listed ⇒ the refreshed review hasn't landed yet.
    if (owned.includes(pending.alias)) return;
    aliasFocusAfter.current = null;
    const next =
      pending.next === null
        ? undefined
        : aliasButtons.current.get(aliasButtonKey(pending.itemId, pending.next));
    (next ?? mergeButtons.current.get(pending.itemId) ?? submitRef.current)?.focus();
  }, [aliasesKey]);

  function removeAlias(owner: CatalogReviewItem['item'], alias: string) {
    if (saving) return;
    setConfirmStatus(null);
    const at = owner.aliases.indexOf(alias);
    aliasFocusAfter.current = {
      itemId: owner.id,
      alias,
      next: owner.aliases[at + 1] ?? owner.aliases[at - 1] ?? null,
    };
    const failed = (message?: string) => {
      aliasFocusAfter.current = null;
      setConfirmStatus({
        kind: 'error',
        message: message ?? (
          <>
            Couldn’t remove alias <bdi>{alias}</bdi> from <bdi>{owner.name}</bdi>. Check your
            connection and try again.
          </>
        ),
      });
    };
    unmerge.mutate(
      { params: { id: store.id }, body: { itemId: owner.id, alias } },
      {
        // As for merge: handle a non-2xx on either the success or error channel.
        onSuccess: (res) => {
          if (res.status === 200) {
            setConfirmStatus({
              kind: 'ok',
              message: (
                <>
                  Alias <bdi>{alias}</bdi> removed from <bdi>{owner.name}</bdi>. It’s a separate
                  item again, listed under “Needs a price”.
                </>
              ),
            });
          } else if (res.status === 409) failed(errorMessage(res) ?? UNMERGE_CONFLICT_FALLBACK);
          else failed();
        },
        onError: (err) => {
          if (errorStatus(err) === 409) failed(errorMessage(err) ?? UNMERGE_CONFLICT_FALLBACK);
          else failed();
        },
      },
    );
  }

  const focusAfter = useRef<{
    kind: ConfirmKind;
    leaving: readonly string[];
    next: string | null;
  } | null>(null);

  // The rows each confirm group currently holds, serialised for the effect deps
  // (option keys contain `|`, so JSON rather than a joined string). A confirmed
  // option leaves the group either way: its key changes with its price, or it
  // stays but loses `needsPrice`.
  const optionNeedsPriceRows = optionRows.filter((row) => row.needsPrice);
  const provisionedKey = JSON.stringify(provisionedRows.map((row) => row.item.id));
  const needsPriceOptionKey = JSON.stringify(optionNeedsPriceRows.map((row) => row.optionKey));
  useEffect(() => {
    const pending = focusAfter.current;
    if (!pending) return;
    const remaining = JSON.parse(
      pending.kind === 'item' ? provisionedKey : needsPriceOptionKey,
    ) as string[];
    // Still in the group ⇒ the refreshed review hasn't landed yet.
    if (pending.leaving.some((id) => remaining.includes(id))) return;
    const button = (id: string | null | undefined) =>
      id == null ? undefined : confirmButtons.current.get(`${pending.kind}:${id}`);
    const target = button(pending.next) ?? button(remaining[0]) ?? submitRef.current;
    // The submit fallback is `disabled` until the save settles; wait for it.
    if (target === null || (target === submitRef.current && saving)) return;
    focusAfter.current = null;
    target.focus();
  }, [provisionedKey, needsPriceOptionKey, saving]);

  /**
   * The row's current draft as a confirming edit, or `null` when the draft isn't
   * a usable price (blank, non-numeric, negative base, % outside 0–100) — so an
   * in-place confirm never saves a half-typed value as "reviewed".
   */
  function confirmEditFor(row: CatalogReviewItem): CatalogEdit | null {
    const edit = edits[row.item.id];
    const pctRaw = edit?.foodCostPct ?? String(row.item.foodCostPct);
    const baseRaw = edit?.basePrice ?? String(row.item.basePrice);
    if (pctRaw.trim() === '' || baseRaw.trim() === '') return null;
    const foodCostPct = Number(pctRaw);
    const basePrice = Number(baseRaw);
    if (!Number.isFinite(foodCostPct) || foodCostPct < 0 || foodCostPct > 100) return null;
    if (!Number.isFinite(basePrice) || basePrice < 0) return null;
    return { itemId: row.item.id, foodCostPct, basePrice, confirm: true };
  }

  /**
   * Confirm rows IN PLACE, staying on Step 2: one `saveCatalog` carrying only
   * these rows, each `confirm: true` at its current draft values.
   * - `item` — provisioned items as `edits`; the server flips them
   *   `archived → active` and the awaited refetch moves them into the main table.
   * - `option` — needs-a-price option rows as `optionEdits` (fan-out to every
   *   linked product, `edits: []`); the refetch drops them from the group.
   * An unusable draft blocks the whole save with the error in the live region.
   * Other rows' unsaved drafts (`edits` / `optionDrafts`) stay untouched.
   */
  function confirmInPlace(target: ConfirmTarget, nextFocus: string | null) {
    if (saving || target.rows.length === 0) return;
    const copy = CONFIRM_COPY[target.kind];
    const invalid: string[] = [];
    let ids: string[];
    let names: string[];
    let body: { edits: CatalogEdit[]; optionEdits?: CatalogOptionEdit[] };
    if (target.kind === 'item') {
      const edits: CatalogEdit[] = [];
      for (const row of target.rows) {
        const edit = confirmEditFor(row);
        if (edit) edits.push(edit);
        else invalid.push(row.item.name);
      }
      ids = edits.map((edit) => edit.itemId);
      names = target.rows.map((row) => row.item.name);
      body = { edits };
    } else {
      const optionEdits: CatalogOptionEdit[] = [];
      for (const row of target.rows) {
        const edit = optionEditFor(row, true);
        if (edit) optionEdits.push(edit);
        else invalid.push(row.name);
      }
      ids = optionEdits.map((edit) => edit.optionKey);
      names = target.rows.map((row) => row.name);
      body = { edits: [], optionEdits };
    }
    if (invalid.length > 0) {
      setConfirmStatus({
        kind: 'error',
        message:
          invalid.length === 1 ? (
            <>
              Enter {copy.fields} for <bdi>{invalid[0]}</bdi> first.
            </>
          ) : (
            <>
              Enter {copy.fields} for {copy.count(invalid.length)} first:{' '}
              {invalid.map((name, i) => (
                <Fragment key={i}>
                  {i > 0 && ', '}
                  <bdi>{name}</bdi>
                </Fragment>
              ))}
              .
            </>
          ),
      });
      return;
    }
    const single = names.length === 1 ? (names[0] ?? null) : null;
    focusAfter.current = { kind: target.kind, leaving: ids, next: nextFocus };
    setConfirming({ kind: target.kind, ids });
    setConfirmStatus(null);
    const failed = () => {
      focusAfter.current = null;
      setConfirmStatus({
        kind: 'error',
        message: single ? (
          <>
            Couldn’t confirm <bdi>{single}</bdi>. Check your connection and try again.
          </>
        ) : (
          `Couldn’t confirm ${copy.count(ids.length)}. Check your connection and try again.`
        ),
      });
    };
    confirmItems.mutate(
      { params: { id: store.id }, body },
      {
        onSuccess: (res) => {
          if (res.status !== 200) return failed();
          setConfirmStatus({
            kind: 'ok',
            message: single ? (
              <>
                <bdi>{single}</bdi> {copy.doneOne}
              </>
            ) : (
              `${copy.count(ids.length)} ${copy.doneMany}`
            ),
          });
        },
        onError: failed,
        onSettled: () => setConfirming(null),
      },
    );
  }

  /** Where focus goes when `itemId` leaves the group: next row, else previous. */
  function focusTargetAfter(itemId: string): string | null {
    const ids = provisionedRows.map((row) => row.item.id);
    const at = ids.indexOf(itemId);
    return ids[at + 1] ?? ids[at - 1] ?? null;
  }

  /**
   * One catalog row (+ its inline back-date editor row), shared by the menu
   * table and the provisioned group so both edit through the same inputs.
   * `provisioned` adds the base-price provenance caption (Req 6.3), wired to the
   * base input via `aria-describedby`, and emphasises the base input when the
   * server flags the item `needsPrice` (estimate or unset).
   */
  function renderCatalogRow(row: CatalogReviewItem, provisioned: boolean) {
    const edit = edits[row.item.id];
    const imgSrc = catalogImageUrl(row.item.imagePath) ?? row.item.imageUrl;
    const backdateOpen = backdateItemId === row.item.id;
    const provenance = provisioned ? provenanceLabel(row.item.baseProvenance) : undefined;
    const provenanceId = `${groupHeadingId}-prov-${row.item.id}`;
    return (
      <Fragment key={row.item.id}>
        <tr>
          <td className="catalog-thumb-cell">
            {imgSrc ? (
              <img
                className="catalog-thumb"
                src={imgSrc}
                alt={row.item.name}
                loading="lazy"
                width={44}
                height={44}
              />
            ) : (
              <span className="catalog-thumb catalog-thumb--empty" aria-hidden="true" />
            )}
          </td>
          {/* `data-label` repeats the column header for the stacked mobile layout
              of the provisioned table (CSS-only, hidden from AT — the header and
              each input's aria-label already name the field). */}
          <td className="catalog-name-cell">
            {row.item.name}
            {row.item.aliases.length > 0 && (
              <>
                {/* Merge badge (Req 8.7): icon + alias count, the word for AT. */}
                {' '}
                <span className="catalog-alias-badge">
                  <svg
                    className="catalog-alias-badge__icon"
                    viewBox="0 0 16 16"
                    width="12"
                    height="12"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M2 5h9m0 0L8.5 2.5M11 5 8.5 7.5M14 11H5m0 0 2.5-2.5M5 11l2.5 2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="num">{row.item.aliases.length}</span>
                  <span className="sr-only">
                    {row.item.aliases.length === 1 ? ' merged name' : ' merged names'}
                  </span>
                </span>
                {/* Each alias is a secondary name with its own Remove (Req 8.5, 8.6). */}
                <ul className="catalog-aliases" aria-label={`Aliases of ${row.item.name}`}>
                  {row.item.aliases.map((alias) => (
                    <li key={alias} className="catalog-alias">
                      <bdi className="catalog-alias__name">{alias}</bdi>
                      <button
                        type="button"
                        className="link catalog-alias__remove"
                        ref={registerAlias(row.item.id, alias)}
                        aria-label={`Remove alias ${alias}`}
                        aria-disabled={saving || undefined}
                        onClick={() => removeAlias(row.item, alias)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </td>
          <td data-label="Food cost %">
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              aria-label={`${row.item.name} food cost %`}
              value={edit?.foodCostPct ?? String(row.item.foodCostPct)}
              onChange={(e) => setField(row.item.id, 'foodCostPct', e.target.value)}
            />
          </td>
          <td data-label={foodCostHeader(currency)}>
            <input
              type="number"
              min={0}
              step="any"
              aria-label={`${row.item.name} food cost value`}
              data-testid={`food-cost-value-${row.item.id}`}
              value={
                edit?.foodCostValue ??
                foodCostValueFor(
                  edit?.foodCostPct ?? String(row.item.foodCostPct),
                  edit?.basePrice ?? String(row.item.basePrice),
                ).toFixed(2)
              }
              onChange={(e) =>
                setFoodCostValue(
                  row.item.id,
                  edit?.basePrice ?? String(row.item.basePrice),
                  e.target.value,
                )
              }
            />
          </td>
          <td data-label="Base price">
            <div className="catalog-base">
              <input
                type="number"
                min={0}
                step="any"
                className={row.needsPrice ? 'catalog-base__input--needs-price' : undefined}
                aria-label={`${row.item.name} base price`}
                aria-describedby={provenance ? provenanceId : undefined}
                value={edit?.basePrice ?? String(row.item.basePrice)}
                onChange={(e) => setField(row.item.id, 'basePrice', e.target.value)}
              />
              {provenance && (
                <span
                  id={provenanceId}
                  className="catalog-provenance"
                  data-needs-price={row.needsPrice || undefined}
                >
                  {provenance}
                </span>
              )}
            </div>
          </td>
          <td data-label="Platform price" data-testid={`platform-price-${row.item.id}`}>
            {money(platformPriceFor(edit?.basePrice ?? String(row.item.basePrice)))}
          </td>
          <td className="catalog-actions-cell">
            <div className="catalog-row-actions">
              {provisioned && (
                // In-place confirm (Req 6.3): saves just this item at the values
                // in its row and moves it to the catalog, staying on Step 2.
                // `aria-disabled` (not `disabled`) while a save runs, so focus
                // stays on the pressed button instead of dropping to <body>.
                <button
                  type="button"
                  className="sm catalog-confirm"
                  ref={registerConfirm('item', row.item.id)}
                  aria-label={
                    isConfirming('item', row.item.id)
                      ? `Saving price for ${row.item.name}`
                      : `Confirm price for ${row.item.name}`
                  }
                  aria-disabled={saving || undefined}
                  onClick={() =>
                    confirmInPlace({ kind: 'item', rows: [row] }, focusTargetAfter(row.item.id))
                  }
                >
                  {isConfirming('item', row.item.id) ? 'Saving…' : 'Confirm'}
                </button>
              )}
              <button
                type="button"
                className="link catalog-merge"
                ref={registerMerge(row.item.id)}
                aria-haspopup="dialog"
                aria-label={`Merge ${row.item.name} into another item`}
                disabled={items.length < 2}
                onClick={() => openMerge(row.item.id)}
              >
                Merge into…
              </button>
              <button
                type="button"
                className="link"
                aria-expanded={backdateOpen}
                aria-label={`Back-date food cost for ${row.item.name}`}
                onClick={() => {
                  setBackdateResult(null);
                  setBackdateItemId(backdateOpen ? null : row.item.id);
                }}
              >
                {backdateOpen ? 'Close' : 'Back-date…'}
              </button>
            </div>
          </td>
        </tr>
        {backdateOpen && (
          <tr className="catalog-backdate-row">
            <td colSpan={7}>
              <BackdateEditor
                store={store}
                item={row.item}
                result={backdateResult?.itemId === row.item.id ? backdateResult : null}
                onApplied={() => {
                  setBackdateResult(null);
                  setBackdateItemId(null);
                }}
                onConflict={(message) =>
                  setBackdateResult({ itemId: row.item.id, conflict: message, failed: false })
                }
                onFailed={() =>
                  setBackdateResult({ itemId: row.item.id, conflict: null, failed: true })
                }
              />
            </td>
          </tr>
        )}
      </Fragment>
    );
  }

  return (
    <form className="card" onSubmit={onSave} aria-label="Step 2 — catalog review">
      <h3>Step 2 — catalog review</h3>
      <p className="muted">
        Platform price is base × (1 + {pct(markupPct)} markup). Edit or confirm each item.
      </p>
      <QueryState isLoading={catalog.isLoading} isError={catalog.isError} label="catalog">
        {items.length === 0 && notConnected ? (
          <div role="alert" className="error" aria-label={`${platformLabel} not connected`}>
            <p>
              {connection === 'expired'
                ? `Your ${platformLabel} session has expired. Re-log in to ${platformLabel} in your Chrome, then retry.`
                : `${platformLabel} isn’t connected. Connect it in your Chrome, then retry.`}
            </p>
            <button
              type="button"
              onClick={() => void catalog.refetch()}
              disabled={catalog.isFetching}
            >
              {catalog.isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        ) : (
          <>
          <CatalogTabs
            active={catalogTab}
            onSelect={setCatalogTab}
            counts={{ items: needsPriceCount, options: optionsNeedPriceCount }}
          >
          {catalogTab === 'options' ? (
            <OptionsPanel
              rows={optionRows}
              markupPct={markupPct}
              draftFor={optionDraftValues}
              onDraftChange={setOptionField}
              saving={saving}
              isConfirming={(key) => isConfirming('option', key)}
              confirmingMany={confirmingMany('option')}
              registerConfirm={(key) => registerConfirm('option', key)}
              onConfirm={(rows, nextFocus) => confirmInPlace({ kind: 'option', rows }, nextFocus)}
            />
          ) : items.length === 0 ? (
          review?.catalogNote ? (
            <div className="card-note" role="note">
              <p>{review.catalogNote}</p>
            </div>
          ) : (
            <p className="muted">No catalog items to review.</p>
          )
        ) : (
          <>
          {confirmedRows.length > 0 && (
            <div className="catalog-scroll">
              <table className="data-table catalog-review" aria-label="Catalog review">
                <CatalogReviewHead currency={currency} />
                <tbody>{confirmedRows.map((row) => renderCatalogRow(row, false))}</tbody>
              </table>
            </div>
          )}
          {provisionedRows.length > 0 && (
            // The provisioned-from-orders group (catalog-coverage Req 6.2, 6.3):
            // its own labelled region + table so the owner (and a screen-reader
            // user, via the heading) lands straight on what needs pricing. It
            // sits BELOW the menu so the late-arriving orders fetch never shifts
            // the rows being edited.
            <section className="catalog-group" aria-labelledby={groupHeadingId}>
              <div className="catalog-group__head">
                <h4 id={groupHeadingId} className="catalog-group__title">
                  Needs a price{' '}
                  <span className="catalog-group__sub">
                    — seen in your orders, not in your menu
                  </span>
                </h4>
                <p className="catalog-group__tally">
                  {needsPriceCount > 0 && (
                    // The server's `needsPrice` count (estimate / unset base),
                    // distinct from the group total (Req 6.4).
                    <span className="catalog-group__badge">
                      <span className="num">{needsPriceCount}</span>{' '}
                      {needsPriceCount === 1 ? 'item needs a price' : 'items need a price'}
                    </span>
                  )}
                  <span className="catalog-group__count">
                    <span className="num">{provisionedRows.length}</span>{' '}
                    {provisionedRows.length === 1 ? 'item' : 'items'} to review
                  </span>
                </p>
              </div>
              {provisionedRows.length > 1 && (
                // Confirm-all (Req 6.4): one save confirming every row at the
                // values shown. The helper text is the (non-modal) confirmation
                // that this accepts estimates as they stand.
                <div className="catalog-group__bar">
                  <p id={groupHelpId} className="catalog-group__help">
                    Set the real base price or food cost % and confirm each row, or confirm them
                    all at the values shown, estimates included. You can change any price later.
                  </p>
                  <button
                    type="button"
                    className="sm catalog-confirm"
                    aria-describedby={groupHelpId}
                    aria-disabled={saving || undefined}
                    onClick={() => confirmInPlace({ kind: 'item', rows: provisionedRows }, null)}
                  >
                    {confirmingMany('item')
                      ? 'Confirming…'
                      : `Confirm all ${provisionedRows.length} items`}
                  </button>
                </div>
              )}
              <table
                className="data-table catalog-review catalog-review--provisioned"
                aria-labelledby={groupHeadingId}
              >
                <CatalogReviewHead currency={currency} actionsLabel="Confirm, merge or back-date" />
                <tbody>{provisionedRows.map((row) => renderCatalogRow(row, true))}</tbody>
              </table>
            </section>
          )}
          </>
          )}
          </CatalogTabs>
          {(items.length > 0 || optionRows.length > 0) && (
            // Persistent polite live region for in-place confirms: it outlives
            // the group (which disappears once every row is confirmed) AND the
            // tab panels, so an announcement survives a tab switch.
            <p
              className="catalog-confirm-status"
              role="status"
              aria-live="polite"
              data-state={confirmStatus?.kind}
            >
              {confirmStatus?.message}
            </p>
          )}
          {mergeSource !== undefined && (
            <MergePicker
              source={mergeSource}
              targets={items.filter((row) => row.item.id !== mergeSource.id).map((row) => row.item)}
              pending={merge.isPending}
              error={mergeError}
              onCancel={closeMerge}
              onConfirm={(target) => confirmMerge(mergeSource, target)}
            />
          )}
          </>
        )}
      </QueryState>
      {needsPriceCount > 0 && (
        // Non-blocking (Req 6.4): the owner may continue with estimates.
        <p id={pendingNoteId} className="catalog-pending-note">
          <span className="num">{needsPriceCount}</span>{' '}
          {needsPriceCount === 1 ? 'item still needs' : 'items still need'} a price. You can
          continue now and price {needsPriceCount === 1 ? 'it' : 'them'} later.
        </p>
      )}
      <div className="wizard-actions">
        {onBack && (
          // Wizard-only step-back; the standalone Catalog surface has no step 1.
          <button type="button" onClick={onBack}>
            Back
          </button>
        )}
        <button
          type="submit"
          ref={submitRef}
          aria-describedby={needsPriceCount > 0 ? pendingNoteId : undefined}
          disabled={saving || catalog.isLoading || notConnected}
        >
          {saveCatalog.isPending
            ? 'Saving…'
            : onNext
              ? 'Confirm catalog & continue'
              : 'Save catalog'}
        </button>
      </div>
      {submitError != null && (
        <p role="alert" className="error">
          {submitError}
        </p>
      )}
      {saveCatalog.isError && (
        <p role="alert" className="error">
          Could not save catalog edits.
        </p>
      )}
    </form>
  );
}

// ─── Step 2 — Items / Options tabs (clarity-catalog-options Req 9.1–9.3) ─────

type CatalogTabKey = 'items' | 'options';

const CATALOG_TABS: readonly { key: CatalogTabKey; label: string }[] = [
  { key: 'items', label: 'Items' },
  { key: 'options', label: 'Options' },
];

/** The writing direction in effect for `el`: the closest `[dir]`, else the document's. */
function directionOf(el: Element): 'ltr' | 'rtl' {
  const dir = el.closest('[dir]')?.getAttribute('dir') ?? document.dir;
  return dir?.toLowerCase() === 'rtl' ? 'rtl' : 'ltr';
}

/**
 * The Step-2 Items / Options tabs (Req 9.1–9.3): an ARIA tablist with a roving
 * tabindex (only the selected tab is in the tab order) and automatic activation
 * — Left/Right (mirrored under RTL), Home and End move focus AND selection.
 * Each label carries its needs-a-price count (`.num` + a visually hidden
 * suffix). Only the active panel renders (`children`); drafts live in `Step2`,
 * above the panels, so a tab switch loses nothing (Req 9.8).
 */
function CatalogTabs({
  active,
  onSelect,
  counts,
  children,
}: {
  active: CatalogTabKey;
  onSelect: (key: CatalogTabKey) => void;
  counts: Record<CatalogTabKey, number>;
  children: ReactNode;
}) {
  const baseId = useId();
  const tabId = (key: CatalogTabKey) => `${baseId}-tab-${key}`;
  const panelId = (key: CatalogTabKey) => `${baseId}-panel-${key}`;
  const tabRefs = useRef(new Map<CatalogTabKey, HTMLButtonElement>());

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const at = CATALOG_TABS.findIndex((t) => t.key === active);
    const last = CATALOG_TABS.length - 1;
    // Under RTL the visual order is mirrored, so ArrowLeft moves FORWARD.
    const forward = directionOf(e.currentTarget) === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backward = forward === 'ArrowRight' ? 'ArrowLeft' : 'ArrowRight';
    let next: number;
    if (e.key === forward) next = at === last ? 0 : at + 1;
    else if (e.key === backward) next = at === 0 ? last : at - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    else return;
    e.preventDefault();
    const key = CATALOG_TABS[next]?.key;
    if (key === undefined) return;
    onSelect(key);
    tabRefs.current.get(key)?.focus();
  }

  return (
    <div className="catalog-tabs-wrap">
      <div className="catalog-tabs" role="tablist" aria-label="Catalog">
        {CATALOG_TABS.map((tab) => {
          const selected = tab.key === active;
          const count = counts[tab.key];
          return (
            <button
              key={tab.key}
              ref={(el) => {
                if (el) tabRefs.current.set(tab.key, el);
                else tabRefs.current.delete(tab.key);
              }}
              type="button"
              role="tab"
              id={tabId(tab.key)}
              aria-selected={selected}
              aria-controls={panelId(tab.key)}
              tabIndex={selected ? 0 : -1}
              className={`catalog-tab${selected ? ' catalog-tab--active' : ''}`}
              onClick={() => onSelect(tab.key)}
              onKeyDown={onKeyDown}
            >
              {tab.label}
              {/* A whitespace node between label and badge: collapsed by the
                  flex layout (the gap spaces them visually), but it keeps the
                  accessible name "Options 3 need a price", not "Options3…". */}
              {count > 0 && ' '}
              {count > 0 && (
                <span className="catalog-tab__badge">
                  <span className="num">{count}</span>
                  <span className="sr-only">{count === 1 ? ' needs a price' : ' need a price'}</span>
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={panelId(active)}
        aria-labelledby={tabId(active)}
        tabIndex={0}
        className="catalog-tabpanel"
      >
        {children}
      </div>
    </div>
  );
}

// ─── Step 2 — in-place confirm + Options panel (catalog-options Req 9.4–9.10) ─

/** What an in-place confirm saves: provisioned items, or needs-a-price option rows. */
type ConfirmKind = 'item' | 'option';
type ConfirmTarget =
  | { kind: 'item'; rows: readonly CatalogReviewItem[] }
  | { kind: 'option'; rows: readonly CatalogOptionRow[] };

/** The live-region wording per confirm kind (the item copy is unchanged). */
const CONFIRM_COPY: Record<
  ConfirmKind,
  { fields: string; count: (n: number) => string; doneOne: string; doneMany: string }
> = {
  item: {
    fields: 'a base price and a food cost % from 0 to 100',
    count: (n) => `${n} items`,
    doneOne: 'confirmed and moved to your catalog.',
    doneMany: 'confirmed and moved to your catalog.',
  },
  option: {
    fields: 'an add-on price and a food cost % from 0 to 100',
    count: (n) => `${n} add-ons`,
    doneOne: 'confirmed for every product that offers it.',
    doneMany: 'confirmed for every product that offers them.',
  },
};

/**
 * An option row's raw input drafts (keyed by `optionKey` in `Step2`). Each field
 * is present only once typed; absent ⇒ the row's own value.
 */
interface OptionDraft {
  priceDelta?: string;
  foodCostPct?: string;
}

const RATE_STATE_LABEL: Record<CatalogOptionRow['rateState'], string> = {
  inherited: 'Inherited',
  override: 'Override',
  mixed: 'Mixed',
};

/**
 * The food-cost % input's placeholder — what a blank input means (inherit):
 * - `inherited` — the linked products' effective rate when they all agree,
 *   else "varies";
 * - `mixed` — "mixed" (blank leaves each option's own rate as it is);
 * - `override` — "inherits" (blanking clears the override).
 */
function optionRatePlaceholder(row: CatalogOptionRow): string {
  if (row.rateState === 'mixed') return 'mixed';
  if (row.rateState === 'override') return 'inherits';
  const rates = new Set(row.linked.map((l) => Math.round(l.effectiveFoodCostPct * 100) / 100));
  const [only] = rates;
  if (rates.size === 1 && only !== undefined) return String(only);
  return rates.size === 0 ? 'inherits' : 'varies';
}

/**
 * The linked products of an option row (Req 9.5): the first three names, then
 * a disclosure button revealing the rest (`aria-expanded` + `aria-controls` on
 * a list that stays in the DOM, `hidden` while collapsed).
 */
function LinkedProducts({ linked }: { linked: CatalogOptionRow['linked'] }) {
  const [open, setOpen] = useState(false);
  const restId = useId();
  if (linked.length === 0) return <span className="muted">—</span>;
  const shown = linked.slice(0, 3);
  const rest = linked.slice(3);
  const entry = (l: CatalogOptionRow['linked'][number]) => (
    <li key={`${l.itemId}:${l.optionId}`}>
      <bdi>{l.itemName}</bdi>
    </li>
  );
  return (
    <div className="catalog-option-linked">
      <ul className="catalog-option-linked__list">{shown.map(entry)}</ul>
      {rest.length > 0 && (
        <>
          <ul id={restId} className="catalog-option-linked__list" hidden={!open}>
            {rest.map(entry)}
          </ul>
          <button
            type="button"
            className="link"
            aria-expanded={open}
            aria-controls={restId}
            onClick={() => setOpen((was) => !was)}
          >
            {open ? 'Show fewer' : `+${rest.length} more`}
            <span className="sr-only"> linked products</span>
          </button>
        </>
      )}
    </div>
  );
}

/**
 * The Options tab panel (Req 6.8, 9.4–9.10). Rows come from the server
 * (one per `optionKey`); drafts and the confirm machinery live in `Step2` and
 * arrive as props, so the panel unmounting on a tab switch loses nothing.
 * - Priced rows sit in the main "Options review" table.
 * - `needsPrice` rows sit in their own labelled group BELOW it (mirroring the
 *   Items needs-a-price group): provenance caption on the price input, per-row
 *   Confirm, and Confirm-all when there is more than one.
 */
function OptionsPanel({
  rows,
  markupPct,
  draftFor,
  onDraftChange,
  saving,
  isConfirming,
  confirmingMany,
  registerConfirm,
  onConfirm,
}: {
  rows: readonly CatalogOptionRow[];
  markupPct: number;
  draftFor: (row: CatalogOptionRow) => Required<OptionDraft>;
  onDraftChange: (optionKey: string, field: keyof OptionDraft, value: string) => void;
  saving: boolean;
  isConfirming: (optionKey: string) => boolean;
  confirmingMany: boolean;
  registerConfirm: (optionKey: string) => (el: HTMLButtonElement | null) => void;
  onConfirm: (rows: readonly CatalogOptionRow[], nextFocus: string | null) => void;
}) {
  const headingId = useId();
  const helpId = useId();
  if (rows.length === 0) {
    return (
      <p className="muted catalog-empty">No options found in your menu or your orders yet.</p>
    );
  }
  const pricedRows = rows.filter((row) => !row.needsPrice);
  const needsPriceRows = rows.filter((row) => row.needsPrice);

  // Two rows can share a name at different prices (`optionKey` embeds the
  // price): disambiguate their accessible labels with the price.
  const nameCounts = new Map<string, number>();
  for (const row of rows) nameCounts.set(row.name, (nameCounts.get(row.name) ?? 0) + 1);
  const labelFor = (row: CatalogOptionRow) =>
    (nameCounts.get(row.name) ?? 0) > 1 ? `${row.name} (${money(row.priceDelta)})` : row.name;

  /** Where focus goes when `optionKey` leaves the group: next row, else previous. */
  function focusTargetAfter(optionKey: string): string | null {
    const keys = needsPriceRows.map((row) => row.optionKey);
    const at = keys.indexOf(optionKey);
    return keys[at + 1] ?? keys[at - 1] ?? null;
  }

  function renderRow(row: CatalogOptionRow, index: number, inGroup: boolean) {
    const draft = draftFor(row);
    const label = labelFor(row);
    const provenance = inGroup ? optionProvenanceLabel(row.baseProvenance) : undefined;
    // An index, not the key: `optionKey` holds spaces, which would split an IDREF.
    const provenanceId = `${headingId}-prov-${index}`;
    const priceBlank = draft.priceDelta.trim() === '';
    const price = Number(draft.priceDelta);
    const platform = Number.isFinite(price) ? price * (1 + markupPct / 100) : 0;
    return (
      <tr key={row.optionKey}>
        <td className="catalog-name-cell">
          <bdi>{row.name}</bdi>
        </td>
        <td data-label="Price">
          <div className="catalog-base">
            <input
              type="number"
              min={0}
              step="any"
              className={`num${row.needsPrice ? ' catalog-base__input--needs-price' : ''}`}
              aria-label={`${label} add-on price`}
              aria-describedby={provenance ? provenanceId : undefined}
              placeholder={isUnpricedOption(row) ? '—' : undefined}
              value={draft.priceDelta}
              onChange={(e) => onDraftChange(row.optionKey, 'priceDelta', e.target.value)}
            />
            {provenance && (
              <span
                id={provenanceId}
                className="catalog-provenance"
                data-needs-price={row.needsPrice || undefined}
              >
                {provenance}
              </span>
            )}
          </div>
        </td>
        <td data-label="Platform price" className="num">
          {priceBlank ? '—' : money(platform)}
        </td>
        <td data-label="Rate">
          <span className="catalog-option-state" data-state={row.rateState}>
            {RATE_STATE_LABEL[row.rateState]}
          </span>
        </td>
        <td data-label="Food cost %">
          <input
            type="number"
            min={0}
            max={100}
            step="any"
            className="num"
            aria-label={`${label} add-on food cost %`}
            placeholder={optionRatePlaceholder(row)}
            value={draft.foodCostPct}
            onChange={(e) => onDraftChange(row.optionKey, 'foodCostPct', e.target.value)}
          />
        </td>
        <td data-label="Linked products">
          <LinkedProducts linked={row.linked} />
        </td>
        {inGroup && (
          <td className="catalog-actions-cell">
            {/* `aria-disabled` (not `disabled`) while a save runs, so focus
                stays on the pressed button instead of dropping to <body>. */}
            <button
              type="button"
              className="sm catalog-confirm"
              ref={registerConfirm(row.optionKey)}
              aria-label={
                isConfirming(row.optionKey)
                  ? `Saving price for ${label}`
                  : `Confirm price for ${label}`
              }
              aria-disabled={saving || undefined}
              onClick={() => onConfirm([row], focusTargetAfter(row.optionKey))}
            >
              {isConfirming(row.optionKey) ? 'Saving…' : 'Confirm'}
            </button>
          </td>
        )}
      </tr>
    );
  }

  const head = (withActions: boolean) => (
    <thead>
      <tr>
        <th>Option</th>
        <th>Price</th>
        <th>Platform price</th>
        <th>Rate</th>
        <th>Food cost %</th>
        <th>Linked products</th>
        {withActions && <th aria-label="Confirm" />}
      </tr>
    </thead>
  );

  return (
    <>
      {pricedRows.length > 0 && (
        <div className="catalog-scroll">
          <table className="data-table catalog-review catalog-options" aria-label="Options review">
            {head(false)}
            <tbody>{pricedRows.map((row, i) => renderRow(row, i, false))}</tbody>
          </table>
        </div>
      )}
      {needsPriceRows.length > 0 && (
        <section className="catalog-group" aria-labelledby={headingId}>
          <div className="catalog-group__head">
            <h4 id={headingId} className="catalog-group__title">
              Needs a price{' '}
              <span className="catalog-group__sub">— seen in your orders, not in your menu</span>
            </h4>
            <p className="catalog-group__tally">
              <span className="catalog-group__badge">
                <span className="num">{needsPriceRows.length}</span>{' '}
                {needsPriceRows.length === 1 ? 'add-on needs a price' : 'add-ons need a price'}
              </span>
            </p>
          </div>
          {needsPriceRows.length > 1 && (
            <div className="catalog-group__bar">
              <p id={helpId} className="catalog-group__help">
                Set the real add-on price or food cost % and confirm each row, or confirm them all
                at the values shown. Add-ons with no price yet need one first. You can change any
                price later.
              </p>
              <button
                type="button"
                className="sm catalog-confirm"
                aria-describedby={helpId}
                aria-disabled={saving || undefined}
                onClick={() => onConfirm(needsPriceRows, null)}
              >
                {confirmingMany ? 'Confirming…' : `Confirm all ${needsPriceRows.length} add-ons`}
              </button>
            </div>
          )}
          {/* Scrolls sideways only in the band where the panel is wider than the
              stacked-card breakpoint but narrower than all seven columns. */}
          <div className="catalog-scroll">
            <table
              className="data-table catalog-review catalog-options catalog-options--needs-price"
              aria-labelledby={headingId}
            >
              {head(true)}
              <tbody>{needsPriceRows.map((row, i) => renderRow(row, i, true))}</tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

/** The shared column header for both Step-2 catalog tables (menu + provisioned). */
function CatalogReviewHead({
  currency,
  actionsLabel = 'Merge or back-date',
}: {
  currency: string | undefined;
  actionsLabel?: string;
}) {
  return (
    <thead>
      <tr>
        <th aria-label="Image" />
        <th>Item</th>
        <th>Food cost %</th>
        <th>{foodCostHeader(currency)}</th>
        <th>Base price</th>
        <th>Platform price</th>
        <th aria-label={actionsLabel} />
      </tr>
    </thead>
  );
}

/** The food-cost value column's header (shared by the `<th>` and the stacked row label). */
function foodCostHeader(currency: string | undefined): string {
  return `Food cost${currency ? ` (${currency})` : ' (value)'}`;
}

/**
 * Where a provisioned item's estimated base came from (catalog-coverage Req
 * 4.4, 6.3), in the owner's words. Absent provenance (an older archived item)
 * shows no caption rather than guessing.
 */
function provenanceLabel(provenance: CatalogBaseProvenance | undefined): string | undefined {
  switch (provenance) {
    case 'menu':
      return 'Menu price';
    case 'line-price':
      return 'From order price';
    case 'order-residual':
      return 'Worked out from your orders';
    case 'sibling-median':
      return 'Estimate (typical item price)';
    case 'unset':
      return 'No price yet';
    default:
      return undefined;
  }
}

/**
 * Where a provisioned add-on's estimated price came from (catalog-options Req
 * 5.5–5.7). Same words as items, except the median estimate, which for an
 * add-on is the same-name add-on's price on other products, not an item price.
 */
function optionProvenanceLabel(
  provenance: CatalogBaseProvenance | undefined,
): string | undefined {
  switch (provenance) {
    case 'sibling-median':
      return 'Estimate (same add-on elsewhere)';
    case 'order-residual':
      return 'Estimate (from your orders)';
    default:
      return provenanceLabel(provenance);
  }
}

/** An add-on provisioned with no price to go on (`unset`): shown blank, not `0`. */
function isUnpricedOption(row: CatalogOptionRow): boolean {
  return row.needsPrice && row.baseProvenance === 'unset';
}

// ─── Merge picker (catalog-item-aliases Req 8.1–8.3, 8.8, 8.9) ───────────────

const MERGE_CONFLICT_FALLBACK = 'That merge conflicts with another item’s name or alias.';
const UNMERGE_CONFLICT_FALLBACK =
  'That alias can’t be restored because it conflicts with another item.';

/** The server's `{ body: { message } }` error text, from a response or a rejection. */
function errorMessage(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object' && 'body' in payload) {
    const body = (payload as { body?: { message?: unknown } }).body;
    if (body && typeof body.message === 'string' && body.message !== '') return body.message;
  }
  return undefined;
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal picker for "Merge into…": lists every OTHER item of the store as a
 * radio target, filterable by name (`aria-label="Filter merge targets"`).
 * `role="dialog"` + `aria-modal`, portalled to <body> (the catalog is itself a
 * <form>, so Enter in the filter must not submit it). Focus starts on the filter,
 * Tab is trapped inside, Escape / backdrop / Cancel close it; the parent returns
 * focus to the trigger. The picker stays open on a conflict, showing the
 * server's message (which names the clashing alias and its owner).
 */
function MergePicker({
  source,
  targets,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  source: CatalogReviewItem['item'];
  targets: readonly CatalogReviewItem['item'][];
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (target: CatalogReviewItem['item']) => void;
}) {
  const titleId = useId();
  const helpId = useId();
  const errorId = useId();
  const listId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    filterRef.current?.focus();
  }, []);

  const needle = filter.trim().toLowerCase();
  const shown = needle === '' ? targets : targets.filter((t) => t.name.toLowerCase().includes(needle));
  // A selection filtered out of view is not a selection: Merge must act on what's shown.
  const selected = shown.find((t) => t.id === selectedId);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      className="merge-picker__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="merge-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={helpId}
        onKeyDown={onKeyDown}
      >
        <h3 id={titleId} className="merge-picker__title">
          Merge <bdi>{source.name}</bdi> into…
        </h3>
        <p id={helpId} className="muted merge-picker__help">
          <bdi>{source.name}</bdi> becomes an alias of the item you pick, so both names share one
          food cost, base price and set of options.
        </p>
        <input
          ref={filterRef}
          type="text"
          className="merge-picker__filter"
          aria-label="Filter merge targets"
          aria-controls={listId}
          placeholder="Filter by name"
          autoComplete="off"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            // Never let Enter in the filter submit an enclosing form.
            if (e.key === 'Enter') {
              e.preventDefault();
              if (selected) onConfirm(selected);
            }
          }}
        />
        <div
          id={listId}
          className="merge-picker__list"
          role="radiogroup"
          aria-label="Merge targets"
        >
          {shown.map((t) => (
            <label key={t.id} className="merge-picker__option">
              <input
                type="radio"
                name={listId}
                checked={selected?.id === t.id}
                onChange={() => setSelectedId(t.id)}
              />
              <bdi>{t.name}</bdi>
            </label>
          ))}
        </div>
        <p className="muted merge-picker__empty" role="status">
          {shown.length === 0 ? 'No matching items.' : ''}
        </p>
        {error !== null && (
          // Visible copy of the outcome announced in the page's polite live
          // region (which sits behind this modal).
          <p id={errorId} className="error merge-picker__error">
            {error}
          </p>
        )}
        <div className="merge-picker__actions">
          <button type="button" onClick={onCancel} aria-disabled={pending || undefined}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!selected}
            aria-disabled={pending || undefined}
            aria-describedby={error !== null ? errorId : undefined}
            onClick={() => selected && onConfirm(selected)}
          >
            {pending ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Back-dated food-cost editor (clarity-reconciliation Req 11.4–11.6) ──────

/**
 * Inline editor for a BACK-DATED food-cost change (Req 11.4–11.6): the user picks
 * a new food-cost % AND an effective date, and the server inserts a new effective-
 * dated catalog version by splitting the covering version — append-only, so
 * orders on/after the date re-cost with the new rate and earlier orders keep the
 * old one (Req 11.5). When the item is `archived` (auto-provisioned, needs
 * review), a confirm checkbox offers to flip it to `active` as part of the edit
 * (Req 11.6). The date is a native date picker; a rejected date (on an existing
 * boundary / outside any version) surfaces the server's `409` message.
 */
function BackdateEditor({
  store,
  item,
  result,
  onApplied,
  onConflict,
  onFailed,
}: {
  store: StoreResponse;
  item: CatalogReviewItem['item'];
  /** The latest edit outcome for THIS item, held by the parent (survives re-render). */
  result: { conflict: string | null; failed: boolean } | null;
  onApplied: () => void;
  onConflict: (message: string) => void;
  onFailed: () => void;
}) {
  const edit = useEditCatalogFoodCost(store.id);
  const isArchived = item.status === 'archived';

  const [foodCostPct, setFoodCostPct] = useState(String(item.foodCostPct));
  // Default the picker to today (a plausible "effective now"); the user back-dates
  // it as needed. `<input type="date">` yields `YYYY-MM-DD`.
  const [effectiveDate, setEffectiveDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [confirmArchived, setConfirmArchived] = useState(false);

  function extractMessage(payload: unknown): string | undefined {
    if (payload && typeof payload === 'object' && 'body' in payload) {
      const body = (payload as { body?: { message?: string } }).body;
      if (body && typeof body.message === 'string') return body.message;
    }
    return undefined;
  }

  function onApply() {
    // Project the chosen calendar day to an ISO instant (start of the day, UTC).
    const effectiveFrom = new Date(`${effectiveDate}T00:00:00.000Z`).toISOString();
    edit.mutate(
      {
        params: { id: store.id },
        body: {
          itemId: item.id,
          foodCostPct: Number(foodCostPct),
          effectiveFrom,
          ...(isArchived && confirmArchived ? { confirmArchived: true } : {}),
        },
      },
      {
        // ts-rest may carry a non-2xx on the success channel ({status, body}) OR
        // reject it onto onError — handle both. The outcome is reported UP to the
        // (stable) Step-2 parent so a rejected date surfaces even if this inline
        // editor re-renders when the catalog cache settles (Req 11.4).
        onSuccess: (res) => {
          if (res.status === 200) onApplied();
          else if (res.status === 409) onConflict(extractMessage(res) ?? 'That effective date conflicts.');
          else onFailed();
        },
        onError: (err) => {
          if (errorStatus(err) === 409) onConflict(extractMessage(err) ?? 'That effective date conflicts.');
          else onFailed();
        },
      },
    );
  }

  return (
    // A DIV (not a nested <form> — the catalog review is itself a form, and
    // nested forms are invalid: an inner submit would bubble to the outer save).
    <div
      role="form"
      className="catalog-backdate"
      aria-label={`Back-date food cost for ${item.name}`}
    >
      <label>
        New food cost %
        <input
          type="number"
          min={0}
          max={100}
          step="any"
          value={foodCostPct}
          onChange={(e) => setFoodCostPct(e.target.value)}
          required
        />
      </label>
      <label>
        Effective from
        <input
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          required
        />
      </label>
      {isArchived && (
        <label className="catalog-backdate-confirm">
          <input
            type="checkbox"
            checked={confirmArchived}
            onChange={(e) => setConfirmArchived(e.target.checked)}
          />
          Mark reviewed (set active)
        </label>
      )}
      <button type="button" onClick={onApply} disabled={edit.isPending}>
        {edit.isPending ? 'Applying…' : 'Apply back-dated edit'}
      </button>
      {result?.conflict != null && (
        <p role="alert" className="error">
          {result.conflict}
        </p>
      )}
      {result?.failed && (
        <p role="alert" className="error">
          Could not apply the back-dated edit.
        </p>
      )}
    </div>
  );
}

// ─── Gather-step live progress helpers (shared glyph + per-source count) ──────

/** The glyph for a source's state — a quiet, text-based status marker (RTL-safe). */
function stateGlyph(state: DeriveSourceStatus['state']): string {
  switch (state) {
    case 'done':
      return '✓';
    case 'running':
      return '…';
    case 'failed':
      return '×';
    case 'skipped':
      return '✓';
    default:
      return '·';
  }
}

/**
 * One source's running count as a short, RTL-safe label. Detailed shows
 * "N/6 months" (total known once the window is split); statements/payouts show a
 * running "N" count; orders/catalog read as a bare "✓" when done. `done` is the
 * figure in a `.num` face so the counts align.
 */
function SourceCount({ s }: { s: DeriveSourceStatus }) {
  if (s.source === 'detailed') {
    return (
      <span className="muted">
        <span className="num">{s.done}</span>
        {s.total !== null ? (
          <>
            /<span className="num">{s.total}</span>
          </>
        ) : null}{' '}
        months
      </span>
    );
  }
  if (s.source === 'statements' || s.source === 'payouts') {
    return (
      <span className="muted">
        <span className="num">{s.done}</span> {s.source === 'statements' ? 'statements' : 'payouts'}
      </span>
    );
  }
  // orders / catalog: a single per-source milestone.
  return <span className="muted">{s.state === 'done' ? 'done' : s.state}</span>;
}

// ─── Step 3 — derived read-only profile (read persisted summary, no pull) ────

function Step3({ store, onBack }: { store: StoreResponse; onBack: () => void }) {
  // Own-platform display name (mythings-breakdown-reconciliation Req 1).
  const { platformLabel } = platformLabels(store.platformKey);
  // FINISH onboarding (onboarding Req 5): once the gate is set, the user leaves
  // the wizard for the store dashboard. The `OnboardingLockGuard` only unlocks
  // other surfaces after the store read flips `onboardingComplete`, so the
  // wizard has to route there itself rather than relying on the user to find the
  // rail — this is the explicit exit the review step was missing.
  const navigate = useNavigate();
  const { clientId, storeId } = useParams();
  // Set true when the user clicks Finish on a store whose gate is NOT yet set:
  // we kick the `derive` (which completes onboarding server-side) and remember
  // to navigate to the dashboard once it resolves. A ref, not state, so it does
  // not re-trigger the completion effect on unrelated re-renders.
  const finishAfterDerive = useRef(false);
  function goToDashboard() {
    if (clientId && storeId) {
      navigate(`/c/${clientId}/store/${storeId}/dashboard`);
    }
  }
  // READ-ONLY summary (unified-data-gathering Req 4.1, 4.2): the
  // `Data_Gathering_Step` pulled + persisted every source up front, so Step 3
  // READS the persisted derived summary and NEVER triggers a pull. `getDerived`
  // recomputes the summary from the store's persisted orders/statements/payouts/
  // catalog at read time — a pure DB read, no `runReferencePull`, no `deps.pull*`.
  const derive = useDeriveProfile(store.id);
  // RECALCULATE (onboarding-automation Req 2) — recompute the food-cost /
  // economic summary from the persisted data after a catalog confirm/edit,
  // WITHOUT re-completing onboarding or re-pulling. A pure refresh of the figures.
  const recalc = useRecalculate(store.id);
  // Read the PERSISTED summary (recomputed from the store's persisted records,
  // no pull) — only queried for a store that has reference data.
  const persisted = useDerivedSummary(store.id, store.referenceDataPresent);
  const persistedResult =
    persisted.data?.status === 200 ? persisted.data.body : null;
  // The persisted read is in flight with nothing cached — first open, or right
  // after a Step-2 catalog save dropped the (now-wrong) cached summary (catalog-
  // coverage Req 6.5). Show a loading line rather than the first-run "Run
  // derivation" prompt, so a recompute is never mistaken for "no data yet".
  const loadingPersisted = store.referenceDataPresent && persisted.isPending;

  // The `derive` mutation finalizes onboarding (it sets the gate) and, because
  // the server made it a PURE READ (unified-data-gathering Req 4.1–4.3), it
  // recomputes the summary + verdict from the ALREADY-PERSISTED records without
  // pulling. `failedSources` is therefore always empty and the old
  // `blocked_failed_pull` 409 is never returned by this read, so there is no
  // pull-failure/retry/proceed-anyway state here — only the verdict the recompute
  // reports (completed / completed-with-commission-unavailable).
  const result: OnboardingDeriveResult | undefined =
    derive.data?.status === 200 ? derive.data.body : undefined;
  // The live recompute result takes precedence; otherwise fall back to the
  // PERSISTED summary read so reopening Step 3 renders the summary instantly.
  const summary: DerivedProfileSummary | undefined =
    result?.derived ?? persistedResult?.derived ?? undefined;
  const months = result?.months ?? persistedResult?.months ?? [];
  const verdict = result?.verdict;
  // Completion covers BOTH outcome (c) `completed` and outcome (b)
  // `completed_commission_unavailable` — a new store finishes too (Req 5.3).
  const completed =
    verdict === 'completed' || verdict === 'completed_commission_unavailable';
  // A new store completed, but commission isn't available yet (shown read-only).
  const commissionUnavailableButDone = verdict === 'completed_commission_unavailable';
  // The store is already onboarded (the gate is set on the persisted read), so
  // the review step is a RE-ENTRY, not a first completion. On this path there is
  // no live `derive` `result`/`verdict` to drive the completion banner, so we
  // read the persisted gate directly — otherwise an already-onboarded store
  // shows only Recalculate/Re-derive/Back with no sign it is done and no way out.
  const alreadyOnboarded = store.onboardingComplete;
  // Show the "complete" treatment + the Finish action whenever onboarding is
  // done, from EITHER signal: a fresh live derive verdict, or the persisted gate.
  const showComplete = completed || alreadyOnboarded;
  // A transport/other error → generic error message.
  const hardError = derive.isError;

  function run() {
    derive.mutate({ params: { id: store.id }, body: {} });
  }

  function recalculate() {
    recalc.mutate({ params: { id: store.id }, body: {} });
  }

  // FINISH onboarding (Option A — the single obvious exit, always available on
  // Step 3). If the store is already onboarded (gate set / a fresh derive
  // already completed this session), finishing is pure navigation. Otherwise
  // finishing RUNS the derive — the server mutation that sets `onboardingComplete`
  // and may auto-enable the store — and we navigate to the dashboard once it
  // resolves (handled by the effect below). This removes the chicken-and-egg
  // where the owner had to press "Re-derive" before any finish action appeared.
  function finish() {
    if (showComplete) {
      goToDashboard();
      return;
    }
    finishAfterDerive.current = true;
    run();
  }

  // Once a Finish-triggered derive completes, leave the wizard for the dashboard.
  // The gate is now set, so the `OnboardingLockGuard` lets the dashboard render.
  useEffect(() => {
    if (finishAfterDerive.current && completed) {
      finishAfterDerive.current = false;
      goToDashboard();
    }
    // `completed` flips from the derive verdict; re-run when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed]);

  // A Finish-triggered derive that errored: clear the pending-navigation intent
  // so the generic error shows and the user can retry rather than silently stall.
  useEffect(() => {
    if (finishAfterDerive.current && derive.isError) {
      finishAfterDerive.current = false;
    }
  }, [derive.isError]);

  return (
    <section className="card" aria-label="Step 3 — derived summary">
      <h3>Step 3 — derived summary</h3>
      {summary === undefined && loadingPersisted && !derive.isPending ? (
        <p className="muted" role="status">
          Loading the latest summary…
        </p>
      ) : summary === undefined ? (
        <>
          <p className="muted">
            Derive the economic profile from the data gathered up front — your statements, detailed
            reports, orders and payouts are already saved.
          </p>
          {derive.isPending ? (
            <p className="muted" role="status">
              Deriving…
            </p>
          ) : (
            <button type="button" onClick={run}>
              Run derivation
            </button>
          )}
        </>
      ) : (
        <>
          <DerivedSummaryWithTabs overview={summary} months={months} store={store} />

          {/*
            Campaigns summary (clarity-campaign-matching): a glance at the
            campaigns this branch participated in over the gathered window —
            how many orders matched a campaign, the campaign-driven sales, and
            the top campaigns by matched orders — with a link into the full
            Campaigns surface. A PURE READ of the already-persisted matches
            (unified-data-gathering): no pull, no re-derive.
          */}
          <CampaignsSummarySection store={store} />

          {/*
            RECALCULATE (onboarding-automation Req 2): recompute the food-cost /
            economic figures from the saved data after confirming or editing the
            catalog — distinct from "Re-derive" (which re-runs the full Step-3
            completion). A pure refresh; it never re-pulls the platform.
          */}
          <div className="derive-recalc">
            <button type="button" onClick={recalculate} disabled={recalc.isPending}>
              {recalc.isPending ? 'Recalculating…' : 'Recalculate'}
            </button>
            {recalc.isSuccess && !recalc.isPending && (
              <span role="status" className="muted">
                Food cost recalculated from the confirmed catalog.
              </span>
            )}
            {recalc.isError && (
              <span role="alert" className="error">
                Could not recalculate.
              </span>
            )}
          </div>

          {result === undefined && (
            // The summary on screen is the PERSISTED recompute (read from the
            // store's persisted records, no pull). Offer an explicit re-derive so
            // the owner can recompute against the current catalog on demand.
            <div className="derive-rerun">
              {derive.isPending ? (
                <p className="muted" role="status">
                  Deriving…
                </p>
              ) : (
                <>
                  <p className="muted">
                    Showing the latest saved summary. Re-derive to recompute from the saved data.
                  </p>
                  <button type="button" onClick={run}>
                    Re-derive
                  </button>
                </>
              )}
            </div>
          )}

          {commissionUnavailableButDone && (
            // Outcome (b): connected new store, no finalized detailed report yet.
            // NOT an error — commission populates once Talabat generates the
            // detailed report; the user can finish (Req 6.3).
            <p role="status" className="notice">
              Commission isn&apos;t available yet — it will populate once {platformLabel} generates the
              store&apos;s detailed report. You can finish onboarding now.
            </p>
          )}

          {completed && result?.autoEnable?.enabled === true ? (
            // AUTO-ENABLE AT WIZARD COMPLETION (onboarding-automation Req 3,
            // option C): completing Step 3 enabled the store for daily ingestion.
            <p role="status" className="success">
              Onboarding complete — this store is now enabled for daily ingestion.
            </p>
          ) : completed && result?.autoEnable?.enabled === false ? (
            // Completed, but the automatic enable was held back (e.g. no / a
            // suspended subscription) — the manual enable stays open (Req 3.3).
            <p role="status" className="notice">
              Onboarding complete. Automatic enable was held back ({result.autoEnable.reason}) — an
              admin can enable this store once that is resolved.
            </p>
          ) : showComplete ? (
            // Covers both a fresh `completed` verdict and the RE-ENTRY path where
            // the store is already onboarded (the persisted gate), so the owner
            // always sees that the store is done and can finish from here.
            <p role="status" className="success">
              Onboarding complete — this store is ready to enable.
            </p>
          ) : null}
        </>
      )}

      {hardError && (
        <p role="alert" className="error">
          Could not run the derivation.
        </p>
      )}

      <div className="wizard-actions">
        <button type="button" onClick={onBack}>
          Back
        </button>
        {/*
          The explicit exit from the wizard (onboarding Req 5), ALWAYS available
          on Step 3 (Option A). If the gate is already set it is pure navigation;
          otherwise it runs the derive that completes onboarding, then navigates
          once that resolves. Disabled while that derive is in flight.
        */}
        <button
          type="button"
          className="primary"
          onClick={finish}
          disabled={finishAfterDerive.current && derive.isPending}
        >
          {finishAfterDerive.current && derive.isPending
            ? 'Finishing…'
            : 'Finish onboarding'}
        </button>
      </div>
    </section>
  );
}

// ─── Step-3 money breakdown graph (clarity-summary-breakdown Req 3) ──────────

/**
 * One graph decomposing the window's gross into where the money goes and what
 * the owner keeps (`clarity-summary-breakdown` Req 3). A stacked horizontal bar
 * (gross = 100%) with the owner's **"Profit"** slice emphasized in teal, each
 * segment labelled with its amount in `.num` tabular figures
 * (currency/locale-formatted via the shared `money` formatter). The bar is
 * purely decorative (`aria-hidden`); the labelled rows + an `.sr-only` sentence
 * carry the accessible equivalent (Req 3.4).
 *
 * Rows are grouped by who the money goes to, each group with a subtotal:
 * - **Restaurants keep** — Profit, Food cost
 * - **Platform commissions** — Commission + commission tax, Online payment fees,
 *   Payment handling, Pro delivery fees, Service fee, Delivery fee, Operational and
 *   waiting charges, Cancelled orders charge, Marketing / Ads
 * - **Not itemized** — whatever is left so the groups add up to the gross
 * - **Discounts to users** — Promos you funded
 * A 0 row is OMITTED (Req 3.6), except "Profit" which always renders even at 0
 * (Req 3.1); a group left with no rows is omitted too.
 * The Talabat-funded discount (`platformFundedDiscount`) is NOT a slice of gross
 * — it is money Talabat contributed, already inside the gross and never deducted
 * from the owner — so it surfaces as a grey memo line ("Paid by Talabat (not
 * deducted)") under Discounts to users when > 0 (Req 3.3): hoverable, but outside
 * the bar and the group total. All-zeros (a new store) renders a muted empty
 * state, never a broken chart (Req 3.5). RTL-safe via logical properties/flow;
 * token-driven ramp, no hard-coded colour.
 */
/** A breakdown group's net direction: kept by the restaurant, taken as a cost, or neutral. */
type GroupDir = 'keep' | 'cost' | 'neutral';

/** One row of the grouped money breakdown (a bar segment + a legend line). */
interface BreakdownRow {
  key: string;
  label: string;
  amount: number;
  color: string;
  /** Plain-language meaning, shown in the hover / focus tooltip. */
  description: string;
  emphasis?: boolean;
  alwaysShow?: boolean;
  /**
   * A small pill after the label (e.g. "Plus" on the Careem subscription line).
   * Carried through the legend value so it reads alongside the amount.
   */
  tag?: string;
  /**
   * The platform does not report this figure for the period
   * (`clarity-careem-ingestion` Req 6.3): render it as an "Unavailable" legend
   * line — NOT a bar segment, NOT `0`-as-fact. Shown so the owner sees the gap
   * honestly. A row with `unavailable:true` always shows (its `amount` is 0 and
   * never drawn on the bar or summed into a group total).
   */
  unavailable?: boolean;
  /**
   * How this line moves the owner's money, shown as a per-row sign indicator so
   * the breakdown is honest even when the platform's reports organise figures
   * differently (the "it doesn't add up" fix):
   *  - `minus`   — DEDUCTED from the owner's money (a cost). The default for a
   *                cost line; the amount reads `−X`.
   *  - `plus`    — ADDED / paid IN to the owner (a credit, e.g. cancellation
   *                compensation, or Profit itself); the amount reads `+X`.
   *  - `neutral` — shown but does NOT move the net: a memo (platform-funded
   *                discount), or a charge that nets to 0 (a charged-and-reversed
   *                fee), or an amount already inside the net. Grey, excluded from
   *                the group total and the bar.
   * `unavailable` rows are their own state (no figure). Defaults to `minus` for a
   * drawn cost; `emphasis` (Profit) counts as `plus`.
   */
  direction?: 'minus' | 'plus' | 'neutral';
}

export function MoneyBreakdownGraph({
  breakdown,
  currency,
  periodLabel,
  finalizedCount,
  cancelledCount,
  platformLabel = 'Talabat',
  subscriberLabel = 'Talabat Pro',
}: {
  breakdown: MoneyBreakdown;
  currency: string | undefined;
  periodLabel: string | null;
  finalizedCount: number;
  cancelledCount: number;
  /** The owning platform's display name (e.g. 'Careem'); defaults to 'Talabat'. */
  platformLabel?: string;
  /** The platform's subscriber-delivery program (e.g. 'Careem Plus' / 'Talabat Pro'). */
  subscriberLabel?: string;
}) {
  const {
    gross,
    commission,
    fees,
    marketing,
    marketingSponsoredDeals,
    restaurantFundedDiscount,
    foodCost,
    proDelivery,
    serviceFee,
    deliveryFee,
    paymentHandling,
    cancellationCharges,
    operationalCharges,
    netToOwner,
    platformFundedDiscount,
    // Careem-only, additive (clarity-careem-ingestion Req 6.2/6.3). ABSENT for
    // Talabat, so every branch below is a no-op there and Talabat rendering is
    // unchanged. `careemPlus` = the period-level Careem Plus deduction line;
    // `unavailable` = the fields Careem doesn't report (shown "Unavailable", not
    // 0); `deliveryFeeDerived` = the delivery fee was derived from the residual.
    careemPlus,
    unavailable,
    deliveryFeeDerived,
    // The single lumped "other Careem adjustments" figure (+ its itemized
    // by-category map) — bank-transfer fees and any future non-CPLUS/non-PROMO
    // category. A real deduction that IS inside netToOwner, so it needs its own
    // named line (and must count toward the itemized total, never the residual).
    otherCareemAdjustments,
    otherCareemAdjustmentsByCategory,
    customerPaidDelivery,
    // MyThings-only (mythings-delivery-fees): the vendor-paid delivery fee
    // (Σ Shipping Discount By Vendor). ABSENT for Talabat/Careem.
    vendorDeliveryFee,
    // Its tier split (Free_Delivery_Tier / Discounted_Delivery_Tier); the two sum
    // to vendorDeliveryFee. ABSENT whenever vendorDeliveryFee is absent.
    vendorDeliveryFeeDiscounted,
    vendorDeliveryFeeFree,
    // MyThings order-status lines (each ABSENT when zero / off MyThings).
    declinedCharges,
    declinedCount,
    qualityDeductions,
    cashCollected,
    cancelledPaidCount,
    saleDiscount,
  } = breakdown;

  // The gap set Careem can't report, as a quick lookup (empty for Talabat).
  const unavailableSet = new Set(unavailable ?? []);
  const isUnavailable = (field: string) => unavailableSet.has(field as never);
  // Careem store signal (the period-level Plus line is present only for Careem).
  const isCareem = careemPlus !== undefined;
  // For Careem, the restaurant does NOT bear the normal delivery fee — the
  // CUSTOMER pays it. The only delivery cost the owner pays is the Careem Plus
  // subscriber-delivery subsidy (its own line). So suppress the "Delivery fee"
  // row entirely for Careem rather than showing a misleading "Unavailable" (or a
  // fabricated residual estimate). Talabat keeps its real delivery-fee line.
  const showDeliveryFee = !isCareem;

  // Which row (`row.key`) or group (`group:<key>`) is hovered / focused. One
  // state drives the bar highlight, the legend highlight and the tooltip, so
  // hovering a bar slice and hovering its legend row are the same interaction.
  const [activeId, setActiveId] = useState<string | null>(null);
  const tipId = useId();
  const fmt = (n: number) => money(n, currency);

  // The gross decomposition, grouped by who the money ends up with. Each group
  // owns a hue family (teal = restaurant, cyan = platform, amber = customers)
  // stepped in lightness per row, so the bar reads as three bands and each band
  // matches its legend group. A zero row is dropped from the bar + legend
  // (Req 3.6); "Profit" is kept even at 0 (Req 3.1), and a group with no rows
  // left is dropped entirely.
  const shade = (hue: string, pct: number) =>
    `color-mix(in srgb, ${hue} ${pct}%, var(--ink-800))`;
  const groupDefs: {
    key: string;
    title: string;
    description: string;
    rows: BreakdownRow[];
    /**
     * The group's net direction: `keep` (stays with the restaurant), `cost` (taken
     * by the platform), `neutral` (shown, nothing deducted). Omit to infer: a group
     * whose rows are all neutral/unavailable is `neutral`, otherwise `cost`.
     */
    dir?: GroupDir;
    /**
     * A grey memo line under the rows: shown, hoverable, but NOT a bar segment and
     * NOT part of the group total (it is money that is not deducted from the owner).
     */
    memo?: { key: string; label: string; description: string; amount: number } | undefined;
  }[] = [
    {
      key: 'restaurant',
      title: 'Restaurants keep',
      description: 'What stays with your restaurant: your profit plus the cost of the food you sold.',
      dir: 'keep',
      rows: [
        {
          key: 'net',
          label: 'Profit',
          description:
            cashCollected !== undefined
              ? `What is left of your sales after every cost below. This is your take-home from these orders, including ${fmt(cashCollected)} cash you collected directly from customers at pickup (${platformLabel} never handled it, so it is not in ${platformLabel}’s payout).`
              : 'What is left of your sales after every cost below. This is your take-home from these orders.',
          amount: netToOwner,
          color: 'var(--teal-300)',
          emphasis: true,
          alwaysShow: true,
        },
        {
          key: 'food',
          label: 'Food cost',
          description: 'What the food you sold cost you to make, from your catalog prices.',
          amount: foodCost,
          color: 'var(--teal-600)',
        },
      ],
    },
    {
      key: 'platform',
      title: 'Platform commissions & fees',
      description: `What ${platformLabel} takes as a cut of each order: commission, the ${subscriberLabel} program, and processing fees.`,
      rows: [
        {
          key: 'commission',
          label: 'Commission + commission tax',
          description: `${platformLabel}’s commission on each order, including the VAT charged on it.`,
          amount: commission,
          color: shade('var(--cyan-400)', 100),
        },
        // SUBSCRIBER-PROGRAM line (Careem Plus / Talabat Pro) — its OWN distinct
        // line right below commission, INSIDE this group but never blended into
        // Marketing / Ads. Violet so it reads as its own thing, not a commission
        // shade. Careem feeds it from the period-level `careemPlus` (cycle feed);
        // Talabat from the per-order `proDelivery`. Shown even at 0 on Careem (a
        // present `careemPlus` means there IS period data to report).
        {
          key: 'subscriber-delivery',
          label: subscriberLabel,
          description:
            careemPlus !== undefined
              ? `The period-level ${subscriberLabel} (subscription program) delivery charge ${platformLabel} deducts, from the billing cycle — a period figure, not a per-order attribution. A platform cost, but its own line below commission, never blended into Marketing / Ads.`
              : `Delivery charges for orders placed by ${subscriberLabel} subscribers.`,
          amount: careemPlus !== undefined ? careemPlus : proDelivery,
          color: 'var(--violet-400, #a78bfa)',
          tag: 'subscription',
          ...(careemPlus !== undefined ? { alwaysShow: true } : {}),
        },
        {
          key: 'fees',
          label: 'Online payment fees',
          description: 'Fees for processing customers’ online card payments.',
          amount: fees,
          color: shade('var(--cyan-400)', 84),
        },
        {
          key: 'payment-handling',
          label: 'Payment handling',
          description: `Payment handling charges from the ${platformLabel} statement for this period.`,
          amount: paymentHandling,
          color: shade('var(--cyan-400)', 72),
        },
        {
          key: 'service',
          label: 'Service fee',
          description: `${platformLabel} service fee from the statement for this period.`,
          amount: serviceFee,
          color: shade('var(--cyan-400)', 50),
        },
        // Delivery fee — Talabat only. For Careem the customer pays delivery, so
        // it is not a cost to the owner and the row is omitted (the subscriber
        // program line above is the only delivery cost the restaurant bears).
        ...(showDeliveryFee
          ? [
              {
                key: 'delivery',
                label: 'Delivery fee',
                description: deliveryFeeDerived
                  ? `Estimated from the residual between gross, commission, fees, promos and the net payout — ${platformLabel} does not report it directly.`
                  : `${platformLabel} delivery fee from the statement for this period.`,
                amount: deliveryFee,
                color: shade('var(--cyan-400)', 42),
                unavailable: isUnavailable('deliveryFee'),
                ...(deliveryFeeDerived ? { tag: 'Estimated' } : {}),
              } satisfies BreakdownRow,
            ]
          : []),
        // Bank-transfer fee and any future non-dispute Careem adjustment category
        // stay here as platform fees (a real charge, its own named line). The
        // DISPUTE/clawback category is handled in the "Order issues" group below,
        // so it is excluded here to avoid showing it twice.
        ...(otherCareemAdjustmentsByCategory
          ? Object.entries(otherCareemAdjustmentsByCategory)
              .filter(([cat, amt]) => amt > 0.005 && cat.toUpperCase() !== 'CLAWBACK')
              .map(
                ([cat, amt], i): BreakdownRow => ({
                  key: `other-adj-${cat.toLowerCase()}`,
                  label: humanizeCareemCategory(cat),
                  description: `A ${humanizeCareemCategory(cat).toLowerCase()} ${platformLabel} deducts for the period, from the billing adjustments — a real charge, shown as its own line rather than lumped into "not itemized".`,
                  amount: amt,
                  color: shade('var(--cyan-400)', 34 - i * 2),
                }),
              )
          : []),
      ],
      // Careem baskets that included the delivery fee the customer paid: that money
      // went to Careem, never to you and never as a cost to you, so it is taken out
      // of the sales figure above and shown here as a grey memo (outside the bar and
      // the group total) instead of being left as an unexplained gap.
      memo:
        customerPaidDelivery !== undefined && customerPaidDelivery > 0.005
          ? {
              key: 'customer-delivery',
              label: 'Delivery paid by customers (not in your sales)',
              description: `${platformLabel}’s basket figure for some orders included the delivery fee the customer paid. ${platformLabel} kept it: it was never paid to you and is not a cost to you, so it is excluded from your sales (${fmt(customerPaidDelivery)}).`,
              amount: customerPaidDelivery,
            }
          : undefined,
    },
    {
      // Marketing & Ads — its OWN section, separate from the subscriber program
      // (which lives in Platform commissions above). Advertising spend only.
      key: 'marketing',
      title: 'Marketing & Ads',
      description: `What you spent on ${platformLabel} advertising — separate from the ${subscriberLabel} program.`,
      // When the platform reports a sponsored/targeted-DEALS split
      // (`marketingSponsoredDeals`, Talabat), show two lines: plain Advertising
      // (search / CPC / premium listing) and Promotional / Sponsored Deals. The
      // two always sum to `marketing`, so the group total is unchanged. When no
      // split is reported (Careem, Talabat periods with none, or marketing is
      // unavailable), keep the single "Marketing / Ads" line.
      rows:
        !isUnavailable('marketing') &&
        typeof marketingSponsoredDeals === 'number' &&
        marketingSponsoredDeals > 0
          ? [
              {
                key: 'marketing-advertising',
                label: 'Advertising',
                description: `Search ads, CPC / keyword and premium listing on ${platformLabel} — the visibility you paid for. The ${subscriberLabel} program is its own line in Platform commissions above.`,
                amount: Math.round((marketing - marketingSponsoredDeals) * 100) / 100,
                color: shade('var(--cyan-400)', 58),
                direction: 'minus',
              },
              {
                key: 'marketing-deals',
                label: 'Promotional / Sponsored Deals',
                description: `Sponsored and targeted deal fees on ${platformLabel} — what you paid to run promotional offers, separate from plain advertising above.`,
                amount: Math.round(marketingSponsoredDeals * 100) / 100,
                color: shade('var(--cyan-400)', 40),
                direction: 'minus',
              },
            ]
          : [
              {
                key: 'marketing',
                label: 'Marketing / Ads',
                description: isUnavailable('marketing')
                  ? `${platformLabel} doesn’t itemize marketing or ad spend, so Clarity shows it as unavailable rather than a zero.`
                  : `What you spent on ${platformLabel} advertising: search ads, premium listing and sponsored deals. Advertising only — the ${subscriberLabel} program is its own line in Platform commissions above.`,
                amount: marketing,
                color: shade('var(--cyan-400)', 58),
                unavailable: isUnavailable('marketing'),
              },
            ],
    },
    {
      // Order issues — what happened on orders that went wrong. Each line
      // declares its DIRECTION so the section is honest without being forced to
      // sum: a charge that is already inside the statement net, or one that was
      // charged-and-reversed, is `neutral` (shown, not double-deducted), while a
      // genuine credit (cancellation compensation) is `plus`. This resolves the
      // "order issues doesn't add up" — the per-order owed-back / operational /
      // wait figures do NOT reconcile with the monthly SOA and are already
      // folded into `netToOwner`, so they inform rather than deduct again.
      key: 'issues',
      title: 'Order issues',
      // MyThings declined charges and missing-item deductions are REAL costs no
      // other line itemizes, so they make the group a cost; otherwise every line
      // is already inside the net and the group stays neutral (unchanged).
      dir: (declinedCharges ?? 0) + (qualityDeductions ?? 0) > 0 ? 'cost' : 'neutral',
      description:
        'What happened on orders that went wrong. Each line shows whether it cost you, paid you, or netted out — figures already inside your net payout are shown neutral, not deducted twice.',
      rows: [
        {
          key: 'cancellations',
          label: 'Cancelled orders charge',
          description: `What ${platformLabel} charged for orders your restaurant cancelled. Already inside your net payout for the period, so it is shown here for visibility, not deducted again.`,
          amount: cancellationCharges,
          color: shade('var(--danger)', 90),
          alwaysShow: true,
          direction: 'neutral',
          ...(cancellationCharges > 0.005 ? { tag: 'in your net' } : {}),
        },
        ...(declinedCharges !== undefined
          ? [
              {
                key: 'declined',
                label: 'Declined orders charge',
                description: `${declinedCount ?? 0} order${declinedCount === 1 ? '' : 's'} your restaurant declined. They are not sales, but ${platformLabel} still charged commission, VAT and the card fee on them — taken from your payout.`,
                amount: declinedCharges,
                color: shade('var(--danger)', 80),
                direction: 'minus',
              } satisfies BreakdownRow,
            ]
          : []),
        {
          key: 'disputes',
          label: 'Customer refunds & quality issues',
          description: `Refunds and quality-dispute clawbacks ${platformLabel} reclaimed for the period. Already inside your net payout; shown here so a dispute is never hidden. 0.00 means none this period.`,
          // The CLAWBACK category from the by-category map (the dispute total),
          // sourced from the payout cycles. 0 when none ⇒ a real, shown zero.
          // MyThings: the per-order "Deduction Order Amount" (missing / wrong
          // items) — a real cost already taken from the payout, so it deducts.
          amount:
            (otherCareemAdjustmentsByCategory &&
              (otherCareemAdjustmentsByCategory.CLAWBACK ??
                otherCareemAdjustmentsByCategory.clawback)) ||
            qualityDeductions ||
            0,
          color: shade('var(--danger)', 66),
          alwaysShow: true,
          direction: qualityDeductions !== undefined ? 'minus' : 'neutral',
        },
        {
          key: 'operational',
          label: 'Operational & waiting charges',
          description: `Per-order operational / wait-time charges — often charged and reversed within the same period (net 0), and in any case already inside your net payout. Shown for visibility, not deducted again.`,
          amount: operationalCharges,
          color: shade('var(--danger)', 46),
          alwaysShow: true,
          direction: 'neutral',
        },
      ],
    },
    {
      key: 'discounts',
      title: 'Discounts to users',
      description: 'Discounts your customers received that you paid for.',
      rows: [
        {
          key: 'promos',
          label: 'Promos you funded',
          description: `Promotions and vouchers your restaurant paid for, not ${platformLabel}.`,
          amount: restaurantFundedDiscount,
          color: 'var(--warn)',
        },
        // MyThings item sale prices: the markdown from menu price the restaurant
        // funded. Gross is at menu prices, so this is a real discount line.
        ...(saleDiscount !== undefined
          ? [
              {
                key: 'sale-prices',
                label: 'Sale prices you funded',
                description: `Items you sold below their menu price on ${platformLabel}: customers paid the sale price, and the difference (${fmt(saleDiscount)}) came out of your sales. Gross sales are shown at menu prices.`,
                amount: saleDiscount,
                color: shade('var(--warn)', 90),
              } satisfies BreakdownRow,
            ]
          : []),
        // MyThings delivery discounts the restaurant funds (Σ Shipping Discount By
        // Vendor), split by program tier. A discount the customer got on delivery,
        // paid by you — already netted out of the payout, so it is itemized here
        // (and in the itemized sum below), never re-subtracted from Profit. ABSENT
        // for Talabat/Careem ⇒ no rows, byte-identical.
        ...(vendorDeliveryFee !== undefined
          ? vendorDeliveryFeeFree !== undefined || vendorDeliveryFeeDiscounted !== undefined
            ? [
                {
                  key: 'delivery-free',
                  label: 'Free delivery',
                  description: `Your share of free delivery for customers on larger orders (the ${platformLabel} free-delivery program). Already taken from your payout.`,
                  amount: vendorDeliveryFeeFree ?? 0,
                  color: shade('var(--warn)', 78),
                } satisfies BreakdownRow,
                {
                  key: 'delivery-discounted',
                  label: 'Discounted delivery',
                  description: `Your share of discounted delivery for customers (the ${platformLabel} discounted-delivery program). Already taken from your payout.`,
                  amount: vendorDeliveryFeeDiscounted ?? 0,
                  color: shade('var(--warn)', 56),
                } satisfies BreakdownRow,
              ]
            : [
                {
                  key: 'delivery-vendor',
                  label: 'Delivery discounts you funded',
                  description: `Your share of customers' delivery discounts on ${platformLabel}. Already taken from your payout.`,
                  amount: vendorDeliveryFee,
                  color: shade('var(--warn)', 70),
                } satisfies BreakdownRow,
              ]
          : []),
        // Careem gives no funder breakdown for discounts ⇒ the platform-funded
        // split is "Unavailable" (not 0): a 0 would read as "the platform funded
        // nothing", a different and false claim (clarity-careem-ingestion Req 6.3).
        ...(isUnavailable('platformFundedDiscount')
          ? [
              {
                key: 'platform-funded-na',
                label: 'Platform-funded promo split',
                description: `${platformLabel} doesn’t report how much of a discount it funded versus you, so the split is shown as unavailable rather than a zero.`,
                amount: 0,
                color: 'var(--fg-faint)',
                unavailable: true,
              } satisfies BreakdownRow,
            ]
          : []),
      ],
      // Talabat's share of the customers' discounts. It is INSIDE the gross and
      // is not deducted from the owner, so it is a grey memo line rather than a
      // slice: shown for completeness, outside the group total and the bar.
      memo:
        platformFundedDiscount > 0
          ? {
              key: 'talabat-funded',
              label: `Paid by ${platformLabel} (not deducted)`,
              description: `Customers got ${fmt(restaurantFundedDiscount + (vendorDeliveryFee ?? 0) + (saleDiscount ?? 0) + platformFundedDiscount)} off in total: ${fmt(restaurantFundedDiscount + (vendorDeliveryFee ?? 0) + (saleDiscount ?? 0))} paid by you and ${fmt(platformFundedDiscount)} paid by ${platformLabel}. The ${platformLabel} part is already inside your gross sales and is not taken from you.`,
              amount: platformFundedDiscount,
            }
          : undefined,
    },
  ];
  // Whatever the rows above don't add up to. The order report and the monthly
  // statement round differently (and Talabat doesn't itemize every fil), so a
  // sliver is normal; showing it keeps the groups adding up to 100% of gross
  // instead of leaving the owner to wonder where the rest went. Only a positive
  // remainder can be drawn.
  // `cancellationCharges` + `operationalCharges` are NO LONGER added here: they
  // are now shown as NEUTRAL "Order issues" lines (already inside `netToOwner` /
  // charged-and-reversed), so adding them to the itemized sum would double-count
  // them against the "Not itemized" residual. `netToOwner` already accounts for
  // them via the statement net.
  const itemized =
    netToOwner +
    foodCost +
    commission +
    fees +
    paymentHandling +
    proDelivery +
    serviceFee +
    deliveryFee +
    marketing +
    restaurantFundedDiscount +
    // The period-level Careem Plus charge + the other Careem adjustments (bank-
    // transfer fees, etc.) are itemized deductions, so they count toward the
    // itemized total and never leak into "Not itemized".
    (careemPlus ?? 0) +
    (otherCareemAdjustments ?? 0) +
    // MyThings vendor-paid delivery fee — mirrors the core breakdown's
    // vendorDeliveryFee emit. A real itemized cost already netted out of the
    // per-order payout, so it counts as itemized and never leaks into the
    // "Not itemized" residual. `?? 0` keeps every non-MyThings breakdown
    // byte-identical (the field is ABSENT ⇒ 0).
    (vendorDeliveryFee ?? 0) +
    // MyThings declined-order charges + missing-item deductions: real costs
    // already inside the payout that no other line carries. `?? 0` keeps every
    // other platform byte-identical. (Cash collected at pickup needs no term:
    // that order's sale is in gross and its cash is in Profit.)
    (declinedCharges ?? 0) +
    (qualityDeductions ?? 0) +
    // MyThings sale-price markdown: added to gross at menu prices, so it must
    // be itemized for the bridge to close.
    (saleDiscount ?? 0);
  const notItemized = Math.round((gross - itemized) * 100) / 100;
  const small = gross > 0 && notItemized / gross <= 0.01;
  // The residual (gross − every named line). For Careem this is NOT an anonymous
  // gap: the billingEntries don't split out the delivery-fee-equivalent, so the
  // residual is predominantly that (plus rounding). Name it per-platform and say
  // what it is, so the owner never sees a nameless number.
  const isCareemBreakdown = isCareem;
  groupDefs.push({
    key: 'other',
    title: 'Not itemized',
    description: `The part of your sales the ${platformLabel} files don't break down into the lines above.`,
    rows: [
      {
        key: 'not-itemized',
        label: small
          ? 'Rounding and small differences'
          : `Not itemized by ${platformLabel}`,
        description: small
          ? 'Small differences between the order report and the monthly statement, which round fees differently.'
          : isCareemBreakdown
            ? `The gap between your per-order sales and what ${platformLabel}’s settlement accounts for${periodLabel ? `, for ${periodLabel}` : ''}. It is NOT a cost taken from you — normal delivery is paid by the customer, not the restaurant (the only delivery cost you bear is the ${subscriberLabel} line above). It is mostly how ${platformLabel} totals the basket differently from the per-order report, plus rounding. Cross-check against your ${platformLabel} payment summary for ${periodLabel ?? 'this period'}.`
            : `Money the ${platformLabel} order report and statement don't break down into the items above.`,
        amount: notItemized > 0.005 ? notItemized : 0,
        color: 'color-mix(in srgb, var(--fg-faint) 55%, var(--ink-800))',
      },
    ],
  });
  // The DIRECTION a row moves the owner's money (the per-line minus/plus/neutral
  // indicator). An explicit `direction` wins; otherwise `emphasis` (Profit) is a
  // `plus` (money kept), an `unavailable` gap has no direction, and every other
  // drawn line defaults to `minus` (a cost). This is presentation-only — the
  // amounts are unchanged; the sign just makes each figure honest.
  const directionOf = (r: BreakdownRow): 'minus' | 'plus' | 'neutral' | 'na' => {
    if (r.unavailable) return 'na';
    if (r.direction) return r.direction;
    if (r.emphasis) return 'plus';
    return 'minus';
  };
  const groups = groupDefs
    .map((g) => {
      // Keep a row when it always shows, has a drawn amount, OR is an
      // "Unavailable" gap line (Careem Req 6.3 — shown, but not a bar segment).
      const rows = g.rows.filter((r) => r.alwaysShow || r.amount > 0 || r.unavailable);
      // The group total is the MAGNITUDE of what this bucket is worth as a share
      // of gross — the sum of its net-moving rows' amounts (neutral + unavailable
      // contribute nothing). It is always a positive figure; the group's DIRECTION
      // (below) carries whether that money is kept or taken, so "Restaurants keep"
      // reads as a positive share, not an arithmetic profit−cost that can go
      // negative when food cost exceeds profit.
      const total = rows.reduce((sum, r) => {
        const dir = directionOf(r);
        return dir === 'plus' || dir === 'minus' ? sum + r.amount : sum;
      }, 0);
      // A group's direction: `keep` (money that stays with the restaurant —
      // teal), `cost` (money taken by the platform — red), or `neutral` (shown,
      // nothing deducted — e.g. Order issues). Explicit per group; defaults to a
      // cost. Drives the header colour + the share figure's framing.
      const dir: GroupDir =
        g.dir ?? (rows.every((r) => directionOf(r) === 'neutral' || directionOf(r) === 'na') ? 'neutral' : 'cost');
      return { ...g, rows, total, dir };
    })
    .filter((g) => g.rows.length > 0 || g.memo !== undefined);
  // Bar segments are the DRAWABLE rows: not "Unavailable", not "neutral" (a
  // neutral line is shown in the legend but never sized on the bar — it has no
  // net-moving figure).
  const segments = groups.flatMap((g) =>
    g.rows.filter((r) => !r.unavailable && directionOf(r) !== 'neutral'),
  );

  // Empty state — a brand-new store with no finalized orders yet (Req 3.5): a
  // muted message, not a zero-width/broken chart. All figures are 0 (a present
  // Careem Plus charge, even at 0, means there IS period data ⇒ not the empty
  // state).
  const allZero =
    careemPlus === undefined &&
    gross === 0 &&
    netToOwner === 0 &&
    commission === 0 &&
    fees === 0 &&
    marketing === 0 &&
    restaurantFundedDiscount === 0 &&
    (vendorDeliveryFee ?? 0) === 0 &&
    (saleDiscount ?? 0) === 0 &&
    foodCost === 0 &&
    proDelivery === 0 &&
    serviceFee === 0 &&
    deliveryFee === 0 &&
    paymentHandling === 0 &&
    cancellationCharges === 0;

  if (allZero) {
    return (
      <section className="card breakdown breakdown--empty" aria-label="Where your money goes">
        <SummaryGroupHead title="Where your money goes" />
        <PeriodLine periodLabel={periodLabel} finalizedCount={finalizedCount} cancelledCount={cancelledCount} declinedCount={declinedCount} cancelledPaidCount={cancelledPaidCount} />
        <p className="muted breakdown__empty">
          No finalized orders yet — the breakdown populates once {platformLabel} generates the detailed
          report.
        </p>
      </section>
    );
  }

  // Bar widths are shares of gross, so the slices read as "of every dinar, this
  // much went here". Guard against a non-positive gross (shouldn't happen past
  // the empty state, but a refund-heavy window could make netToOwner negative):
  // use the sum of the drawn magnitudes as the width basis so the bar always
  // fills and no slice vanishes to a negative width.
  const widthBasis = Math.max(
    gross,
    segments.reduce((sum, s) => sum + Math.max(0, s.amount), 0),
    1,
  );
  const widthOf = (amount: number) => (Math.max(0, amount) / widthBasis) * 100;

  // Where each row (and each whole group) sits on the bar, in % of its width,
  // so the tooltip can centre itself over whatever is hovered or focused.
  const targets = new Map<
    string,
    {
      title: string;
      description: string;
      amount: number;
      color: string;
      center: number;
      /** false for a memo line: it has no slice on the bar, so the bar doesn't dim. */
      onBar: boolean;
      /** true for a Careem "Unavailable" gap row (Req 6.3) — shows no figure. */
      unavailable?: boolean;
    }
  >();
  let cursor = 0;
  for (const g of groups) {
    const groupStart = cursor;
    for (const r of g.rows) {
      // An "Unavailable" gap row OR a "neutral" row (memo / already-in-net /
      // net-zero) has no slice: it sits in the legend only, never dims the bar,
      // and does not advance the cursor.
      if (r.unavailable || directionOf(r) === 'neutral') {
        targets.set(r.key, {
          title: r.tag ? `${r.label} · ${r.tag}` : r.label,
          description: r.description,
          amount: r.amount,
          color: r.color,
          center: cursor,
          onBar: false,
          ...(r.unavailable ? { unavailable: true } : {}),
        });
        continue;
      }
      const size = widthOf(r.amount);
      targets.set(r.key, {
        title: r.label,
        description: r.description,
        amount: r.amount,
        color: r.color,
        center: cursor + size / 2,
        onBar: true,
      });
      cursor += size;
    }
    targets.set(`group:${g.key}`, {
      title: g.title,
      description: g.description,
      amount: g.total,
      color: g.rows[0]?.color ?? 'var(--fg-faint)',
      center: (groupStart + cursor) / 2,
      onBar: g.rows.length > 0,
    });
    if (g.memo) {
      targets.set(g.memo.key, {
        title: g.memo.label,
        description: g.memo.description,
        amount: g.memo.amount,
        color: 'var(--fg-faint)',
        center: (groupStart + cursor) / 2,
        onBar: false,
      });
    }
  }
  const groupOfRow = new Map(groups.flatMap((g) => g.rows.map((r) => [r.key, g.key] as const)));
  const isActiveSegment = (rowKey: string) =>
    activeId !== null && (activeId === rowKey || activeId === `group:${groupOfRow.get(rowKey)}`);
  const active = activeId === null ? undefined : targets.get(activeId);

  // Each segment's share of gross — "of every dinar, this much went here". Always
  // a positive magnitude (a share has no sign). Guard a non-positive gross so a
  // refund-heavy window never divides by zero.
  const shareOfGross = (amount: number) =>
    gross > 0 ? (Math.abs(amount) / gross) * 100 : null;
  // Money is always rendered from its MAGNITUDE — the direction sign is carried
  // by the chip / the group sign, never by `fmt` itself (otherwise a negative
  // value + an explicit sign read as a double minus, e.g. "−−193.18").
  const fmtAbs = (amount: number) => fmt(Math.abs(amount));
  const formatAmountShare = (amount: number) => {
    const share = shareOfGross(amount);
    return share === null ? fmtAbs(amount) : `${fmtAbs(amount)} · ${pct(share)}`;
  };
  // The per-row direction, resolved to a chip: `−` deducted (red), `+` added
  // (green), `○` neutral (faint), `–` unavailable (faint). The chip carries the
  // meaning so the value itself stays an unsigned magnitude.
  const signChar = (dir: 'minus' | 'plus' | 'neutral' | 'na'): string =>
    dir === 'minus' ? '−' : dir === 'plus' ? '+' : dir === 'neutral' ? '○' : '–';
  // The GROUP's direction sign (keep `+`, cost `−`, neutral / zero none),
  // rendered as its own leading element so it stays attached to the figure under
  // RTL (a prepended character would float to the far end). The value itself is
  // an unsigned magnitude + share, wrapped in <bdi> so the Latin number lays out
  // left-to-right inside an Arabic line.
  const groupTotalSign = (total: number, dir: GroupDir): string =>
    total < 0.005 ? '' : dir === 'keep' ? '+' : dir === 'cost' ? '−' : '';
  // The legend value for a row: "Unavailable" for a gap (Req 6.3), else the
  // unsigned amount + share (the chip beside it carries the direction). A tag
  // rides as a text pill on the label.
  const rowLegendValue = (r: BreakdownRow) =>
    r.unavailable ? 'Unavailable' : formatAmountShare(r.amount);
  const rowLegendLabel = (r: BreakdownRow) => (r.tag ? `${r.label} · ${r.tag}` : r.label);

  // The accessible sentence equivalent of the bar (Req 3.4): the owner's take
  // out of gross, then each cost. Read by screen readers; the visual bar is
  // aria-hidden.
  const describe = (label: string, amount: number) => {
    const share = shareOfGross(amount);
    return `${label} ${fmt(amount)}${share === null ? '' : ` (${pct(share)})`}`;
  };
  const srSummary = `Of ${fmt(gross)} gross: ${groups
    .map((g) => {
      const parts = g.rows.map((r) =>
        r.unavailable
          ? `${rowLegendLabel(r)} unavailable`
          : describe(rowLegendLabel(r), r.amount),
      );
      if (g.memo) parts.push(`${describe(g.memo.label, g.memo.amount)}, already inside gross`);
      return `${describe(g.title, g.total)} — ${parts.join(', ')}`;
    })
    .join('; ')}.`;

  return (
    <section className="card breakdown" aria-label="Where your money goes">
      <header className="breakdown__head">
        <div className="breakdown__head-main">
          <SummaryGroupHead title="Where your money goes" />
          <PeriodLine periodLabel={periodLabel} finalizedCount={finalizedCount} cancelledCount={cancelledCount} declinedCount={declinedCount} cancelledPaidCount={cancelledPaidCount} />
        </div>
        <p className="breakdown__gross">
          <span className="breakdown__gross-label">Gross sales</span>
          <span className="breakdown__gross-value num">{fmt(gross)}</span>
        </p>
      </header>

      <p className="sr-only">{srSummary}</p>

      {/* The stacked proportion bar. The bar itself is decorative (aria-hidden);
          the legend below is the accessible data. Hovering a slice shows its
          tooltip, and hovering / focusing a legend row lights up the same slice. */}
      <div className="breakdown__bar-wrap">
        <div
          className="breakdown__bar"
          aria-hidden="true"
          data-hovering={active?.onBar ? '' : undefined}
        >
          {segments.map((s) => (
            <span
              key={s.key}
              className="breakdown__seg"
              data-emphasis={s.emphasis ? '' : undefined}
              data-active={isActiveSegment(s.key) ? '' : undefined}
              style={{ inlineSize: `${widthOf(s.amount)}%`, background: s.color }}
              onMouseEnter={() => setActiveId(s.key)}
              onMouseLeave={() => setActiveId(null)}
            />
          ))}
        </div>
        {active && (
          <div
            id={tipId}
            role="tooltip"
            className="breakdown__tip"
            style={{ insetInlineStart: `${Math.min(86, Math.max(14, active.center))}%` }}
          >
            <p className="breakdown__tip-title">
              <span
                className="breakdown__tip-dot"
                style={{ background: active.color }}
                aria-hidden="true"
              />
              {active.title}
            </p>
            <p className="breakdown__tip-desc">{active.description}</p>
            <p className="breakdown__tip-figures">
              <span className="num">
                {active.unavailable ? 'Unavailable' : fmt(active.amount)}
              </span>
              {!active.unavailable && shareOfGross(active.amount) !== null && (
                <span className="breakdown__tip-share">
                  <span className="num">{pct(shareOfGross(active.amount)!)}</span> of gross sales
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* The labelled amounts (the accessible data), grouped by who the money
          goes to. Each group shows its subtotal, then the design-system Legend
          for its rows (swatch in the row's colour, value in .num). */}
      <div className="breakdown__groups">
        {groups.map((g) => (
          <div key={g.key} className="breakdown__group" role="group" aria-label={g.title}>
            <p
              className="breakdown__group-head"
              data-active={activeId === `group:${g.key}` ? '' : undefined}
              tabIndex={0}
              aria-describedby={activeId === `group:${g.key}` ? tipId : undefined}
              onMouseEnter={() => setActiveId(`group:${g.key}`)}
              onMouseLeave={() => setActiveId(null)}
              onFocus={() => setActiveId(`group:${g.key}`)}
              onBlur={() => setActiveId(null)}
            >
              <span className="breakdown__group-title">{g.title}</span>
              <span className="breakdown__group-total num" data-dir={g.dir}>
                <bdi dir="ltr">
                  {groupTotalSign(g.total, g.dir)}
                  {formatAmountShare(g.total)}
                </bdi>
              </span>
            </p>
            <ul className="breakdown__rows">
              {g.rows.map((r) => {
                const dir = directionOf(r);
                const activeRow = activeId === r.key;
                return (
                  <li
                    key={r.key}
                    className="breakdown__row"
                    data-dir={dir}
                    data-emphasis={r.emphasis ? '' : undefined}
                    data-active={activeRow ? '' : undefined}
                    tabIndex={0}
                    aria-describedby={activeRow ? tipId : undefined}
                    onMouseEnter={() => setActiveId(r.key)}
                    onMouseLeave={() => setActiveId(null)}
                    onFocus={() => setActiveId(r.key)}
                    onBlur={() => setActiveId(null)}
                  >
                    <span className="breakdown__sign" aria-hidden="true">
                      {signChar(dir)}
                    </span>
                    <span className="breakdown__row-label">
                      {r.label}
                      {r.tag && <span className="breakdown__row-tag">{r.tag}</span>}
                    </span>
                    <span className="breakdown__row-val num">
                      <bdi dir="ltr">{rowLegendValue(r)}</bdi>
                    </span>
                  </li>
                );
              })}
            </ul>
            {g.memo && (
              <div className="breakdown__memo">
                <Legend
                  items={[
                    {
                      id: g.memo.key,
                      label: g.memo.label,
                      color: 'transparent',
                      value: formatAmountShare(g.memo.amount),
                    },
                  ]}
                  activeId={activeId}
                  onActiveChange={setActiveId}
                  describedBy={tipId}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The exact-date period + the order-count split shown atop the breakdown:
 * delivered (completed) vs cancelled, so the lumped "finalized" count is broken
 * out. Delivered = `finalizedCount − cancelledCount`.
 */
function PeriodLine({
  periodLabel,
  finalizedCount,
  cancelledCount,
  declinedCount = 0,
  cancelledPaidCount = 0,
}: {
  periodLabel: string | null;
  finalizedCount: number;
  cancelledCount: number;
  /** MyThings: orders the restaurant declined (in the finalized count, not delivered). */
  declinedCount?: number | undefined;
  /** MyThings: cancelled orders MyThings still paid in full (sales, not delivered). */
  cancelledPaidCount?: number | undefined;
}) {
  const delivered = Math.max(0, finalizedCount - cancelledCount - declinedCount - cancelledPaidCount);
  const cancelled = cancelledCount + cancelledPaidCount;
  return (
    <p className="breakdown__period">
      {periodLabel ? (
        <span className="breakdown__period-range" aria-label={`Period ${periodLabel}`}>
          {periodLabel}
        </span>
      ) : (
        <span className="breakdown__period-range muted" aria-label="Period unavailable">
          Period unavailable
        </span>
      )}
      <span className="breakdown__period-count">
        <span className="num">{num(delivered)}</span> delivered
        {cancelled > 0 && (
          <>
            {' · '}
            <span className="num breakdown__period-cancelled">{num(cancelled)}</span> cancelled
            {cancelledPaidCount > 0 && cancelledPaidCount === cancelled ? ' (paid)' : ''}
          </>
        )}
        {declinedCount > 0 && (
          <>
            {' · '}
            <span className="num breakdown__period-cancelled">{num(declinedCount)}</span> declined
          </>
        )}
      </span>
    </p>
  );
}

/**
 * Catalog-match coverage below which the effective food-cost rate is flagged as
 * partial (catalog-coverage Req 5.2): under 90% of order lines matched, an
 * unmatched line contributes no food cost, so the rate understates the truth.
 */
const FOOD_COST_COVERAGE_THRESHOLD = 0.9;

/**
 * How many order lines are still unmatched when coverage is under
 * {@link FOOD_COST_COVERAGE_THRESHOLD}; `null` when coverage is sufficient or
 * there are no lines at all (nothing to qualify). Display-only — the effective
 * `pct` stays `foodCost / gross` (Req 5.3).
 */
function partialFoodCostLines({
  matchedLines,
  totalLines,
}: DerivedProfileSummary['effectiveFoodCost']): number | null {
  if (totalLines <= 0) return null;
  if (matchedLines / totalLines >= FOOD_COST_COVERAGE_THRESHOLD) return null;
  return Math.max(0, totalLines - matchedLines);
}

/**
 * Whether add-on food cost reads as partial (catalog-options Req 8.5): there
 * are ordered add-on tokens and under {@link FOOD_COST_COVERAGE_THRESHOLD} of
 * them are priced. Display-only — `pct` stays `foodCost / gross` (Req 8.3).
 */
function partialAddOnFoodCost({
  pricedOptions,
  totalOptions,
}: DerivedProfileSummary['effectiveFoodCost']): boolean {
  if (totalOptions <= 0) return false;
  return pricedOptions / totalOptions < FOOD_COST_COVERAGE_THRESHOLD;
}

/**
 * The derived summary with a month switcher: "Overview" (all completed months
 * combined) + one tab per completed month (newest first). Selecting a tab swaps
 * which {@link DerivedProfileSummary} the shared {@link DerivedProfileView}
 * renders — so a user can inspect each month alone or the whole picture. The
 * tabs only appear when the server returned more than one month; a single-month
 * derive renders the overview directly.
 */
function DerivedSummaryWithTabs({
  overview,
  months,
  store,
}: {
  overview: DerivedProfileSummary;
  months: readonly MonthSummary[];
  store: StoreResponse;
}) {
  // `null` = Overview; otherwise the month key.
  const [active, setActive] = useState<string | null>(null);
  if (months.length === 0) {
    return <DerivedProfileView summary={overview} store={store} />;
  }
  const activeSummary =
    active === null ? overview : (months.find((m) => m.key === active)?.summary ?? overview);
  return (
    <div className="summary-tabs-wrap">
      <div className="summary-tabs" role="tablist" aria-label="Period">
        <button
          type="button"
          role="tab"
          aria-selected={active === null}
          className={`summary-tab ${active === null ? 'summary-tab--active' : ''}`}
          onClick={() => setActive(null)}
        >
          Overview
        </button>
        {months.map((m) => {
          // The server marks the current partial month with a " · in progress"
          // label suffix. Render that as a distinct live pill (dot + text) rather
          // than plain label text, so the live month reads as its own state.
          const [monthName, progressTag] = m.label.split(' · ');
          const inProgress = progressTag !== undefined;
          return (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={active === m.key}
              className={`summary-tab ${active === m.key ? 'summary-tab--active' : ''}${
                inProgress ? ' summary-tab--live' : ''
              }`}
              onClick={() => setActive(m.key)}
            >
              {monthName}
              {inProgress && (
                <span className="summary-tab__live">
                  <span className="summary-tab__live-dot" aria-hidden="true" /> in progress
                </span>
              )}
            </button>
          );
        })}
      </div>
      <DerivedProfileView summary={activeSummary} store={store} />
    </div>
  );
}

/**
 * The user-facing label for each derived-field provenance (clarity-careem-
 * ingestion Req 4.6), matching the signed-off mock's four states
 * (`brand/careem-profile-review-mock.html`). The WORD carries the meaning (never
 * colour alone — WCAG 1.4.1); the dot is decorative.
 */
const CONFIDENCE_LABEL: Record<FieldConfidence, string> = {
  derived: 'Derived',
  period: 'Period graph',
  blended: 'Blended',
  defaulted: 'Defaulted',
};

/**
 * The derived-vs-default confidence badge (clarity-careem-ingestion Req 4.6).
 * Careem-only: shown beside each derived field in the derived-profile review so
 * the owner sees at a glance whether a figure is derived from their own orders,
 * sourced from the period-level analytics graph, a blended approximation, or a
 * documented default. Follows the house pill `.badge` pattern (the shape +
 * decorative dot the matcher's `ConfidenceMeter` and the lifecycle badges share)
 * but models the mock's four PROVENANCE states, which differ from the matcher's
 * high/medium/low meter. The level WORD carries the meaning (a screen reader
 * reads "Blended" / "Period graph"), so the badge reads without colour.
 */
function ConfidenceBadge({ level }: { level: FieldConfidence }) {
  return (
    <span className={`badge conf-badge conf-badge--${level}`} data-confidence={level}>
      {CONFIDENCE_LABEL[level]}
    </span>
  );
}

/**
 * The read-only derived economic profile (Req 6.1), redesigned (clarity-derived-
 * summary Req 5) into grouped glass panels in the teal-glass Operate world — a
 * lead `.stat` per theme over `.profile-grid` supporting rows, all money/number
 * in tabular `.num` figures, locale/currency-formatted and RTL-safe by logical
 * properties. The groups: Commission, Payments, Discounts, Food cost, Order mix,
 * with the derivation period in the header. Unavailable (muted "not available
 * yet") vs a real `0` (a figure) vs not-configured (muted "set in Step 1") read
 * visually distinct (Req 5.4). The outer region keeps its `aria-label` so the
 * whole derived instrument is one labelled landmark.
 *
 * ─── Careem's additive approximation + confidence layer (clarity-careem-
 * ingestion task 8.2; Req 4.6, 6.4, 9.3) ──────────────────────────────────────
 *
 * When the summary carries a `confidence` map (Careem only — a Talabat summary
 * omits it and this whole layer is inert, so Talabat renders byte-identically),
 * the view adds, per the signed-off mock `brand/careem-profile-review-mock.html`:
 *   - a lead approximation banner making the two Careem approximations explicit
 *     (the blended AOV + the period-level Plus split);
 *   - ONE merged "Order mix — Careem Plus" section holding the Plus / non-Plus
 *     counts, the period-level split (`Period graph` badges on plus_pct /
 *     non_plus_pct), the single blended basket (`Blended` badge + "no per-order
 *     Plus flag" explainer, both buckets the SAME figure — Req 6.4), and the
 *     per-Plus cost. The count-based mix and the period split used to live in two
 *     near-duplicate panels; they are the same period-level figures, so they are
 *     now one section;
 *   - a per-field confidence badge (Derived / Period graph / Blended / Defaulted)
 *     on every field, including the `Defaulted` platform-funded split (Req 4.6).
 */
function DerivedProfileView({
  summary,
  store,
}: {
  summary: DerivedProfileSummary;
  store: StoreResponse;
}) {
  // The Careem confidence map is the single gate for the whole additive layer:
  // present ⇒ a Careem derivation (render badges + the blended/period surfaces);
  // absent ⇒ Talabat (everything below stays inert, byte-identical to today).
  const confidence = summary.confidence;
  const careem = summary.careem;
  const currency = store.profile.currency;
  // Platform_Label_Map (mythings-breakdown-reconciliation Req 1): the store's
  // own-platform display labels, replacing the binary `careem ? … : 'Talabat'`
  // ternary that mislabelled a MyThings store as Talabat.
  const { platformLabel, subscriberLabel } = platformLabels(store.platformKey);
  // Partial-coverage hint (catalog-coverage Req 5.2): the count of still-
  // unmatched lines when coverage is under the threshold, else null.
  const unpricedLines = partialFoodCostLines(summary.effectiveFoodCost);
  const foodCostHintId = useId();
  // Add-on coverage (catalog-options Req 8.4/8.5): a second caption when the
  // period's orders carry add-on tokens, plus a muted hint under the threshold.
  const hasAddOns = summary.effectiveFoodCost.totalOptions > 0;
  const addOnsPartial = partialAddOnFoodCost(summary.effectiveFoodCost);
  const addOnHintId = useId();
  const foodCostDescribedBy =
    [unpricedLines !== null ? foodCostHintId : null, addOnsPartial ? addOnHintId : null]
      .filter((id): id is string => id !== null)
      .join(' ') || undefined;
  // Exact date range of the completed-months period, shown at the TOP in the
  // breakdown header (not just a month badge in the profile section).
  const periodLabel = periodDates(summary.periodStart, summary.periodEnd);

  // Payment per-order cost (Req 2.2), computed here from the pass-through params
  // + the derived online base — shown WITH its parts, never a blended mystery
  // number. Null when any param is unset ⇒ no fabricated cost line (Req 2.4).
  const feesConfigured =
    summary.pay_fee_fixed !== null &&
    summary.pay_fee_pct !== null &&
    summary.pay_fee_tax_pct !== null;
  const perOnlineOrderCost = feesConfigured
    ? (summary.pay_fee_fixed! + (summary.avg_order_online * summary.pay_fee_pct!) / 100) *
      (1 + summary.pay_fee_tax_pct! / 100)
    : null;

  return (
    <div className="summary-groups" aria-label="Derived economic profile">
      {/* Careem lead (Req 6.4 / 9.3): the two approximations made explicit, up
          top, before the numbers — the honesty lead from the signed-off mock.
          Talabat (no confidence map) never renders this. */}
      {confidence && (
        <div className="approx-note" role="note" aria-label="Careem approximations">
          <span className="approx-note__title">Two Careem approximations, made explicit</span>
          <p className="approx-note__body">
            Careem's order data carries no per-order Plus flag, so one blended basket average
            stands in for both buckets. The Plus / non-Plus split comes from a separate
            period-level analytics graph, not the orders.
          </p>
        </div>
      )}

      {/* The headline: where the gross goes and what the owner keeps
          (clarity-summary-breakdown Req 3) — one graph at the top, carrying the
          EXACT date range of the completed-months period. */}
      <MoneyBreakdownGraph
        breakdown={summary.breakdown}
        currency={currency}
        periodLabel={periodLabel}
        finalizedCount={summary.finalizedCount}
        cancelledCount={summary.cancelledCount}
        platformLabel={platformLabel}
        subscriberLabel={subscriberLabel}
      />

      {/* Payouts matched against the orders linked to them (clarity-reconciliation). */}
      <PayoutMatchPanel
        payouts={summary.payouts}
        estimate={summary.estimatedBalance}
        currency={currency}
        platformLabel={platformLabel}
        careem={summary.careem}
      />

      {/* Header: the finalized-count / accuracy context (dates moved up). */}
      <header className="summary-head">
        <h4 className="summary-head__title">Derived economic profile</h4>
        <div className="summary-head__meta">
          <span className="summary-head__count">
            <span className="num">
              {num(
                Math.max(
                  0,
                  summary.finalizedCount -
                    summary.cancelledCount -
                    (summary.breakdown.declinedCount ?? 0) -
                    (summary.breakdown.cancelledPaidCount ?? 0),
                ),
              )}
            </span>{' '}
            delivered
            {summary.cancelledCount > 0 && (
              <>
                {' · '}
                <span className="num">{num(summary.cancelledCount)}</span> cancelled
              </>
            )}{' '}
            used
          </span>
        </div>
      </header>

      <div className="summary-groups__grid">
        {/* Commission (Req 5.1): comm % lead + comm tax %. */}
        <section className="card summary-group" aria-label="Commission">
          <SummaryGroupHead title="Commission" />
          {summary.commissionsUnavailable ? (
            <div className="stat">
              <span className="stat__label">Commission rate</span>
              <span className="stat__value stat__value--muted">Not available yet</span>
              <span className="stat__sub">
                Populates once {platformLabel} generates the detailed report.
              </span>
            </div>
          ) : (
            <>
              <div className="stat">
                <span className="stat__label">
                  Commission rate
                  {confidence && <ConfidenceBadge level={confidence.comm_pct} />}
                </span>
                <span className="stat__value num">
                  {num(summary.comm_pct)}
                  <span className="unit">%</span>
                </span>
              </div>
              <dl className="profile-grid">
                <ProfileRow label="Commission tax" value={pct(summary.comm_tax_pct)} />
              </dl>
            </>
          )}
        </section>

        {/* Payments (Req 2): online share + fee parts + computed per-order cost. */}
        <section className="card summary-group" aria-label="Payments">
          <SummaryGroupHead title="Payments" />
          <div className="stat">
            <span className="stat__label">
              Online / card share
              {confidence && <ConfidenceBadge level={confidence.visa_pct} />}
            </span>
            <span className="stat__value num">
              {num(summary.visa_pct)}
              <span className="unit">%</span>
            </span>
            <span className="stat__sub">
              Avg online order {money(summary.avg_order_online, currency)}
            </span>
          </div>
          {/* Careem payment-gateway fee (Req 4.2): the derived card-fee %, carried
              verbatim on `careem.pay_fee_pct` (the Talabat per-order-cost formula
              below needs the gateway PARTS, which Careem doesn't expose, so the
              single % reads here instead). Derived from the store's own orders. */}
          {careem && careem.pay_fee_pct !== null && (
            <div className="stat">
              <span className="stat__label">
                Payment-gateway fee
                <ConfidenceBadge level={confidence?.pay_fee_pct ?? 'derived'} />
              </span>
              <span className="stat__value num">
                {num(careem.pay_fee_pct)}
                <span className="unit">%</span>
              </span>
              <span className="stat__sub">Gateway fee + tax over gross, card orders only.</span>
            </div>
          )}
          {feesConfigured ? (
            <>
              <dl className="profile-grid">
                <ProfileRow label="Fixed fee / order" value={money(summary.pay_fee_fixed, currency)} />
                <ProfileRow label="Variable fee" value={pct(summary.pay_fee_pct)} />
                <ProfileRow label="Fee tax" value={pct(summary.pay_fee_tax_pct)} />
              </dl>
              <p className="summary-formula">
                <span className="summary-formula__label">Cost per online order</span>
                <span className="summary-formula__value num">
                  {money(perOnlineOrderCost, currency)}
                </span>
                <span className="summary-formula__parts num">
                  ({money(summary.pay_fee_fixed, currency)} + {money(summary.avg_order_online, currency)}{' '}
                  × {num(summary.pay_fee_pct)}%) × (1 + {num(summary.pay_fee_tax_pct)}%)
                </span>
                <span className="summary-formula__note">Online/card orders only — cash carries no gateway fee.</span>
              </p>
            </>
          ) : (
            <p className="summary-unset muted" role="note">
              Payment-gateway fees not configured — set them in Step 1 to see the per-order cost.
            </p>
          )}
        </section>

        {/* Discounts (Req 3): you-funded / Talabat-funded / total (money). */}
        <section className="card summary-group" aria-label="Discounts">
          <SummaryGroupHead title="Discounts" />
          <div className="stat">
            <span className="stat__label">Total funded discount</span>
            <span className="stat__value num">{money(summary.discount_total, currency)}</span>
          </div>
          <dl className="profile-grid">
            <ProfileRow
              label="You funded"
              value={money(summary.discount_restaurant_funded, currency)}
            />
            <ProfileRow
              label={confidence ? 'Platform funded' : 'Talabat funded'}
              value={money(summary.discount_platform_funded, currency)}
              confidence={confidence?.discount_platform_funded}
            />
          </dl>
        </section>

        {/* Food cost (Req 4): the ACTUAL effective rate (from the period's orders)
            leads; the Step-1 default + catalog average are secondary context. */}
        <section className="card summary-group" aria-label="Food cost">
          <SummaryGroupHead title="Food cost" />
          {summary.effectiveFoodCost.pct !== null ? (
            <div className="stat">
              <span className="stat__label">Effective food cost</span>
              <span
                className="stat__value num"
                aria-describedby={foodCostDescribedBy}
              >
                {num(summary.effectiveFoodCost.pct)}
                <span className="unit">%</span>
              </span>
              <span className="stat__sub">
                {money(summary.effectiveFoodCost.amount, currency)} of gross ·{' '}
                {num(summary.effectiveFoodCost.matchedLines)} of{' '}
                {num(summary.effectiveFoodCost.totalLines)} order lines priced
              </span>
              {/* Add-on coverage caption (catalog-options Req 8.4): only when the
                  period's orders carry add-ons, so a menu without options stays quiet. */}
              {hasAddOns && (
                <span className="stat__sub">
                  {num(summary.effectiveFoodCost.pricedOptions)} of{' '}
                  {num(summary.effectiveFoodCost.totalOptions)} add-ons priced
                </span>
              )}
              {/* Under-threshold coverage (Req 5.2): a still-low rate must not
                  read as a verified figure. Plain muted text, not an alert. */}
              {unpricedLines !== null && (
                <span className="stat__hint" id={foodCostHintId}>
                  Partial — {num(unpricedLines)} {unpricedLines === 1 ? "line isn't" : "lines aren't"}{' '}
                  matched to a catalog item yet, so this rate is an estimate until they're priced
                  in Step 2.
                </span>
              )}
              {/* Under-threshold add-on coverage (catalog-options Req 8.5): same
                  muted qualifier as the lines hint. */}
              {addOnsPartial && (
                <span className="stat__hint" id={addOnHintId}>
                  Add-on food cost is partial until the rest are priced in Step 2.
                </span>
              )}
            </div>
          ) : (
            <div className="stat">
              <span className="stat__label">Effective food cost</span>
              <span className="stat__value stat__value--muted">No priced orders yet</span>
              <span className="stat__sub">Populates once orders match the catalog.</span>
            </div>
          )}
          <dl className="profile-grid">
            {summary.default_food_cost_pct !== null && (
              <ProfileRow
                label="Default (fallback)"
                value={pct(summary.default_food_cost_pct)}
              />
            )}
            {summary.catalog_avg_food_cost_pct !== null && (
              <ProfileRow label="Catalog average" value={pct(summary.catalog_avg_food_cost_pct)} />
            )}
          </dl>
        </section>

        {/* Order mix (Req 5.1 / 4.3 / 6.4). ONE Careem-Plus section: the
            Plus / non-Plus counts and the period-level split (share + per-order
            Plus cost) are two views of the SAME figures, so for Careem they live
            together here — counts and shares side by side, one blended basket,
            one Plus cost — instead of being split across two near-duplicate
            panels. The Plus split is a PERIOD-level fact (the orders-by-user-type
            analytics graph, not the orders feed), carried on a `Period graph`
            badge. For Talabat (no `confidence`) the classic two-AOV layout
            stays. */}
        <section className="card summary-group summary-group--wide" aria-label="Order mix">
          <SummaryGroupHead title={confidence ? 'Order mix — Careem Plus' : 'Order mix'} />
          <dl className="profile-grid summary-grid--mix">
            <ProfileRow
              label="Plus / Pro orders"
              value={num(summary.n_plus)}
              confidence={confidence ? confidence.plus_pct : undefined}
            />
            <ProfileRow
              label="Non-Plus orders"
              value={num(summary.n_non_plus)}
              confidence={confidence ? confidence.non_plus_pct : undefined}
            />
            {confidence && careem ? (
              <>
                <ProfileRow
                  label="Plus order share"
                  value={pct(careem.plus_pct)}
                  confidence={confidence.plus_pct}
                />
                <ProfileRow
                  label="Non-Plus order share"
                  value={pct(careem.non_plus_pct)}
                  confidence={confidence.non_plus_pct}
                />
                <ProfileRow
                  label="Average basket (blended)"
                  value={money(summary.avg_order_plus, currency)}
                  confidence={confidence.avg_order_plus}
                />
                <ProfileRow
                  label="Cost per Plus order"
                  value={money(summary.plus_fee, currency)}
                  confidence={confidence.plus_fee}
                />
              </>
            ) : (
              <>
                <ProfileRow label="Avg Plus order" value={money(summary.avg_order_plus, currency)} />
                <ProfileRow
                  label="Avg non-Plus order"
                  value={money(summary.avg_order_non_plus, currency)}
                />
                <ProfileRow label="Cost per Plus order" value={money(summary.plus_fee, currency)} />
              </>
            )}
            <ProfileRow label="Orders used" value={num(summary.finalizedCount)} />
          </dl>
          {/* The two Careem approximations made EXPLICIT (Req 6.4 / 9.3): the
              Plus split is a period-level graph figure (not the orders feed), and
              — with no per-order Plus flag — both buckets share the one blended
              basket average. Surfaced in words, not left implicit. */}
          {confidence && (
            <p className="summary-unset muted" role="note">
              The Plus / non-Plus split comes from the period-level orders-by-user-type analytics
              graph, and both buckets carry the same blended basket average — Careem gives no
              per-order Plus flag.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

/** A group panel heading — the theme name, in the uppercase stat-label voice. */
function SummaryGroupHead({ title }: { title: string }) {
  return <h5 className="summary-group__head">{title}</h5>;
}

/**
 * One definition-list row: muted term + tabular-figure value (`.num`). An
 * optional `confidence` badge (Careem only) rides beside the term — the
 * derived-vs-default provenance badge from the signed-off mock (Req 4.6).
 */
function ProfileRow({
  label,
  value,
  confidence,
}: {
  label: string;
  value: string;
  confidence?: FieldConfidence | undefined;
}) {
  return (
    <div className="profile-row">
      <dt>
        {label}
        {confidence && <ConfidenceBadge level={confidence} />}
      </dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

/**
 * The campaigns glance for Step 3 (clarity-campaign-matching). A PURE READ of
 * the persisted order↔campaign matches for this branch over the gathered window:
 * how many orders matched a campaign, the campaign-driven sales, and the top
 * campaigns by matched orders — with a link into the full Campaigns surface.
 *
 * It uses `useCampaigns(store.id)` (the same read the Campaigns surface uses) and
 * renders nothing while loading, on error, on the not-yet-onboarded 409 (the
 * step is reachable pre-complete), or when no campaign matched — the summary
 * only appears when there is something worth showing, so it never clutters a
 * store with no campaign activity.
 */
function CampaignsSummarySection({ store }: { store: StoreResponse }) {
  const { clientId } = useParams();
  const query = useCampaigns(store.id, !isNotYetOnboarded(store));
  if (query.isLoading || query.isError) return null;
  const data = query.data?.status === 200 ? query.data.body : null;
  if (!data || data.campaigns.length === 0) return null;

  const { rollup } = data;
  const cur = store.profile?.currency ?? 'JOD';

  // Campaigns that actually have matched orders, top 5 by matched count.
  const participated = data.campaigns
    .filter((row) => (row.actuals?.matchedOrders ?? 0) > 0)
    .sort((a, b) => (b.actuals?.matchedOrders ?? 0) - (a.actuals?.matchedOrders ?? 0));

  if (participated.length === 0) return null;
  const top = participated.slice(0, 5);

  return (
    <section className="card campaigns-summary" aria-label="Campaigns this period">
      <div className="campaigns-summary__head">
        <h4>Campaigns this period</h4>
        {clientId && (
          <Link
            className="campaigns-summary__link"
            to={`/c/${clientId}/store/${store.id}/campaigns`}
          >
            View all campaigns →
          </Link>
        )}
      </div>

      <div className="campaigns-summary__stats">
        <div className="campaigns-summary__stat">
          <span className="campaigns-summary__label">Orders matched to a campaign</span>
          <span className="campaigns-summary__value num">
            {num(rollup.ordersMatched)}
            <span className="campaigns-summary__of"> / {num(rollup.ordersTotal)}</span>
          </span>
        </div>
        <div className="campaigns-summary__stat">
          <span className="campaigns-summary__label">Campaign-driven sales</span>
          <span className="campaigns-summary__value num">{money(rollup.campaignDrivenSales, cur)}</span>
        </div>
        <div className="campaigns-summary__stat">
          <span className="campaigns-summary__label">Campaigns participated</span>
          <span className="campaigns-summary__value num">{num(participated.length)}</span>
        </div>
      </div>

      <ul className="campaigns-summary__list">
        {top.map((row) => (
          <li key={row.campaign.id} className="campaigns-summary__row">
            <span className="campaigns-summary__name">
              {row.campaign.name}
              {row.campaign.arabicName && (
                <span className="campaigns-summary__ar" dir="rtl">
                  {row.campaign.arabicName}
                </span>
              )}
            </span>
            <span className="campaigns-summary__matched num">
              {num(row.actuals?.matchedOrders ?? 0)} orders
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
